import React, { createContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { db, useAuth, initFirebase, getFirebaseInstance } from './AuthContext';

const getFirestoreSdk = () => {
  const instance = getFirebaseInstance();
  if (!instance) throw new Error("Firestore SDK accessed before init");
  return instance.firestoreSdk;
};

const getFunctionsSdk = () => {
  const instance = getFirebaseInstance();
  if (!instance) throw new Error("Functions SDK accessed before init");
  return instance.functionsSdk;
};

const resolveDbArg = (arg: any) => {
  const instance = getFirebaseInstance();
  if (arg === db || !arg) {
    return instance?.db;
  }
  return arg;
};

export const collection = (...args: any[]) => {
  args[0] = resolveDbArg(args[0]);
  return (getFirestoreSdk().collection as any)(...args);
};
export const doc = (...args: any[]) => {
  args[0] = resolveDbArg(args[0]);
  return (getFirestoreSdk().doc as any)(...args);
};
export const getDocs = (...args: any[]) => (getFirestoreSdk().getDocs as any)(...args);
export const getDocsFromServer = (...args: any[]) => (getFirestoreSdk().getDocsFromServer as any)(...args);
export const setDoc = (...args: any[]) => (getFirestoreSdk().setDoc as any)(...args);
export const deleteDoc = (...args: any[]) => (getFirestoreSdk().deleteDoc as any)(...args);
export const updateDoc = (...args: any[]) => (getFirestoreSdk().updateDoc as any)(...args);
export const query = (...args: any[]) => (getFirestoreSdk().query as any)(...args);
export const where = (...args: any[]) => (getFirestoreSdk().where as any)(...args);
export const onSnapshot = (...args: any[]) => (getFirestoreSdk().onSnapshot as any)(...args);
export const writeBatch = (...args: any[]) => {
  args[0] = resolveDbArg(args[0]);
  return (getFirestoreSdk().writeBatch as any)(...args);
};
export const addDoc = (...args: any[]) => (getFirestoreSdk().addDoc as any)(...args);
export const orderBy = (...args: any[]) => (getFirestoreSdk().orderBy as any)(...args);
export const limit = (...args: any[]) => (getFirestoreSdk().limit as any)(...args);
export const runTransaction = (...args: any[]) => {
  args[0] = resolveDbArg(args[0]);
  return (getFirestoreSdk().runTransaction as any)(...args);
};
export const getDoc = (...args: any[]) => (getFirestoreSdk().getDoc as any)(...args);
export const Timestamp = new Proxy({} as any, {
  get(_target, prop) {
    const sdk = getFirestoreSdk();
    const val = (sdk.Timestamp as any)[prop];
    return typeof val === 'function' ? val.bind(sdk.Timestamp) : val;
  },
  construct(_target, argArray) {
    const sdk = getFirestoreSdk();
    return new (sdk.Timestamp as any)(...argArray);
  }
});
export const getFunctions = (...args: any[]) => (getFunctionsSdk().getFunctions as any)(...args);
export const httpsCallable = (...args: any[]) => (getFunctionsSdk().httpsCallable as any)(...args);
import {
    Employee, Shift, Attendance, Break, Task, Notification,
    LogbookEntry, AppSettings, Subscription, AuditLogEntry,
    SalesRecord, NotificationSettings, TimekeepingAction, KPIData, EmployeeStatus, Availability, SwapRequest
} from '../types';
import { getPendingActions, savePendingAction, removePendingAction, PendingAction } from '../utils/offlineStore';
import { calculateKPIData } from '../utils/dataUtils';
import { getDateInTimeZone } from '../dateUtils';
import Papa from 'papaparse';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import { notifySlackClockIn, notifySlackClockOut, notifySlackGeofenceBypass, notifySlackTimeOff } from '../utils/slackIntegration';
import { downloadOrShareFile } from '../utils/downloadUtils';

// Define the shape of the context
export interface DataContextType {
    employees: Employee[];
    shifts: Shift[];
    attendance: Attendance[];
    breaks: Break[];
    tasks: Task[];
    logbookEntries: LogbookEntry[];
    notifications: Notification[];
    auditLog: AuditLogEntry[];
    salesRecords: SalesRecord[];
    swapRequests: SwapRequest[];

    settings: AppSettings;
    notificationSettings: NotificationSettings;
    subscription: Subscription | null;

    companyName: string;
    companyLogo: string | null;
    managerPin: string;
    managerPhone: string;
    calendarToken: string | null;

    isLoading: boolean;
    isOnline: boolean;
    pendingSyncCount: number;
    lastSyncTime: string | null;
    isPro: boolean;
    canAddEmployee: boolean;
    isTrialExpired: boolean;
    kpiData: KPIData;

    addEmployee: (employee: Omit<Employee, 'id'>) => Promise<void>;
    updateEmployee: (employee: Employee) => Promise<void>;
    deleteEmployee: (id: string) => Promise<void>;
    importEmployees: (csvString: string) => Promise<{ successCount: number; errorCount: number; errors: string[] }>;
    exportEmployees: (employees: Employee[]) => void;
    generateCalendarSyncToken: (employeeId?: string) => Promise<string>;

    addShift: (shift: any, recurrenceRule: string, recurrenceEndDate?: string) => Promise<void>;
    addShiftsBatch: (shifts: any[]) => Promise<void>;
    updateShift: (shift: Shift) => Promise<void>;
    deleteShift: (id: string) => Promise<void>;
    deleteShiftSeries: (groupId: string, fromDate: string) => Promise<void>;
    publishShifts: (shiftIds: string[]) => Promise<void>;

    toggleEmployeeStatus: (
        employeeId: string,
        action: TimekeepingAction,
        method: string,
        location?: { lat: number, lng: number, accuracy?: number },
        bypassReason?: string,
        photoUrl?: string,
        wifiSSID?: string
    ) => Promise<{ offline: boolean }>;

    updateAttendance: (id: string, data: Partial<Attendance>) => Promise<void>;

    addTask: (task: Omit<Task, 'id'>) => Promise<void>;
    updateTask: (task: Task) => Promise<void>;
    deleteTask: (id: string) => Promise<void>;

    addLogbookEntry: (entry: Omit<LogbookEntry, 'id' | 'createdBy' | 'createdAt'>) => Promise<void>;
    updateLogbookEntry: (entry: LogbookEntry) => Promise<void>;
    deleteLogbookEntry: (id: string) => Promise<void>;

    updateCompanyProfile: (data: { name?: string; logo?: string | null; managerPin?: string; phone?: string; subscription?: Partial<Subscription> }) => Promise<void>;
    updateSettings: (settings: AppSettings) => Promise<void>;
    updateNotificationSettings: (settings: Partial<NotificationSettings>) => Promise<void>;

    updateSubscription: (plan: string, seats?: number, cycle?: string) => Promise<void>;
    cancelSubscription: () => Promise<void>;
    reactivateSubscription: () => Promise<void>;

    markNotificationAsRead: (id: string) => Promise<void>;
    markAllNotificationsAsRead: () => Promise<void>;
    deleteNotification: (id: string) => Promise<void>;
    clearAllNotifications: () => Promise<void>;

    exportAuditLog: (log: AuditLogEntry[]) => void;
    updateSalesRecord: (weekStartDate: string, amount: number) => Promise<void>;

    requestTimeOff: (data: { employeeId: string; type: 'holiday' | 'sick'; startDate: string; endDate: string; reason: string }) => Promise<void>;
    approveTimeOffRequest: (shiftId: string) => Promise<void>;
    denyTimeOffRequest: (shiftId: string) => Promise<void>;
    
    requestShiftSwap: (shiftId: string, fromEmployeeId: string, toEmployeeId: string, reason?: string) => Promise<{ autoApproved: boolean }>;
    approveShiftSwap: (notification: Notification) => Promise<void>;
    denyShiftSwap: (notification: Notification) => Promise<void>;
    broadcastShift: (shiftId: string) => Promise<void>;
    claimShift: (swapRequestId: string, employeeId: string) => Promise<void>;

    triggerSync: () => Promise<void>;
    fetchAttendanceRange: (startDateISO: string, endDateISO: string) => Promise<Attendance[]>;
    activeBroadcast: { message: string, priority: 'normal' | 'emergency', createdAt: string } | null;
}

export const DataContext = createContext<DataContextType | undefined>(undefined);

const defaultSettings: AppSettings = {
    currency: 'GBP',
    language: 'en',
    timezone: 'Europe/London',
    holidayAccrualRate: 12.07,
    workDayHours: 8,
    weeklyOvertimeThreshold: 40,
    overtimeMultiplier: 1.5,
    attendanceRounding: 0,
    snapToShift: false,
    gracePeriod: 5,
    payFrequency: 'weekly',
    geofencingEnabled: false,
    isXeroConnected: false,
    autoDeductEnabled: false,
    autoDeductThreshold: 6,
    autoDeductAmount: 30,
    dailyLaborBudget: 0,
    shiftApprovalRequired: false,
    ssp2026Rules: false,
    sspDailyRate: 24.65,
    wifiVerificationEnabled: false,
    requiredWifiSSID: '',
    requiredIpSubnet: '',
    kioskEmployeeNfcEnabled: false,
};

const defaultNotificationSettings: NotificationSettings = {
    upcomingShifts: true,
    missedClockOuts: true,
    clockInOut: true,
    lateArrivals: true,
    missedClockOutHours: 12,
    overdueTasks: true,
    sicknessAlerts: true,
    sicknessAlertThresholdDays: 3,
    sicknessAlertPeriodDays: 30,
    earlyClockOuts: true,
    earlyClockOutMinutes: 15,
    qualificationExpiry: true,
    excessiveBreaks: true,
};

// Helper to remove undefined keys recursively from an object or array
const removeUndefined = (obj: any): any => {
    if (obj === null || obj === undefined) return undefined;
    if (Array.isArray(obj)) {
        return obj
            .filter(item => item !== undefined)
            .map(item => (typeof item === 'object' && item !== null ? removeUndefined(item) : item));
    }
    if (typeof obj === 'object' && !(obj instanceof Date)) {
        const newObj: any = {};
        for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
                newObj[key] = (typeof value === 'object' && value !== null && !(value instanceof Date))
                    ? removeUndefined(value)
                    : value;
            }
        }
        return newObj;
    }
    return obj;
};

// Helper to calculate working days in a range based on availability
const calculateWorkingDays = (start: Date, end: Date, availability?: Availability): number => {
    const curr = new Date(start);
    curr.setHours(0, 0, 0, 0);
    const endDate = new Date(end);
    endDate.setHours(0, 0, 0, 0);

    let count = 0;
    while (curr <= endDate) {
        const dayIndex = curr.getDay(); // 0 = Sunday
        const daysMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayKey = daysMap[dayIndex] as keyof Availability;

        let isWorkDay = true;
        if (availability) {
            isWorkDay = availability[dayKey]?.isAvailable ?? false;
        } else {
            // Default Mon-Fri if no specific availability set
            isWorkDay = dayIndex !== 0 && dayIndex !== 6;
        }

        if (isWorkDay) count++;
        curr.setDate(curr.getDate() + 1);
    }
    return count;
};

const getNewStatus = (action: TimekeepingAction): EmployeeStatus => {
    switch (action) {
        case 'Clock In': return 'Clocked In';
        case 'Clock Out': return 'Clocked Out';
        case 'Start Break': return 'On Break';
        case 'End Break': return 'Clocked In';
    }
};

// --- Offline helpers ---------------------------------------------------------
const NETWORK_TIMEOUT = 'NETWORK_TIMEOUT';
const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> => new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(NETWORK_TIMEOUT)), ms);
    p.then(v => { clearTimeout(t); resolve(v); }, e => { clearTimeout(t); reject(e); });
});

/**
 * Replays queued (not-yet-synced) offline actions on top of the latest Firestore data,
 * so the UI always reflects the real current state — even after an app restart or when
 * a fresh snapshot arrives. Queue entries are removed only after a successful sync; by then
 * Firestore's local cache already contains the committed write, so there is no flicker.
 */
const applyPendingActions = (
    employees: Employee[],
    attendance: Attendance[],
    breaks: Break[],
    actions: PendingAction[]
): { employees: Employee[]; attendance: Attendance[]; breaks: Break[] } => {
    if (actions.length === 0) return { employees, attendance, breaks };
    let emps = employees, atts = attendance, brks = breaks;
    const sorted = [...actions].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    for (const act of sorted) {
        const ts = act.timestamp;
        const newStatus = getNewStatus(act.action);
        emps = emps.map(e => {
            if (e.id !== act.employeeId) return e;
            const u: any = { ...e, status: newStatus };
            if (act.action === 'Start Break') u.breakStartedAt = ts;
            if (act.action === 'End Break' || act.action === 'Clock Out') u.breakStartedAt = null;
            return u;
        });

        const activeAtt = atts.find(a => a.employeeId === act.employeeId && a.status === 'active');
        if (act.action === 'Clock In') {
            atts = atts.map(a => (a.employeeId === act.employeeId && a.status === 'active')
                ? { ...a, clockOut: ts, status: 'completed' } as Attendance : a);
            atts = [...atts, {
                id: `pending_${act.id}`,
                employeeId: act.employeeId,
                clockIn: ts,
                clockOut: null,
                status: 'active',
                createdAt: ts,
                location: act.location,
                geofenceBypassReason: act.geofenceBypassReason,
                clockInPhotoUrl: act.photoUrl
            } as Attendance];
        } else if (act.action === 'Clock Out') {
            if (activeAtt) {
                atts = atts.map(a => a.id === activeAtt.id
                    ? { ...a, clockOut: ts, status: 'completed', clockOutPhotoUrl: act.photoUrl } as Attendance : a);
                brks = brks.map(b => (b.attendanceId === activeAtt.id && !b.end) ? { ...b, end: ts } : b);
            }
        } else if (act.action === 'Start Break') {
            if (activeAtt) {
                brks = [...brks, { id: `pending_brk_${act.id}`, attendanceId: activeAtt.id, start: ts, end: null } as Break];
            }
        } else if (act.action === 'End Break') {
            if (activeAtt) {
                brks = brks.map(b => (b.attendanceId === activeAtt.id && !b.end) ? { ...b, end: ts } : b);
            }
        }
    }
    return { employees: emps, attendance: atts, breaks: brks };
};

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentUser, employeeUser } = useAuth();

    // Data State
    // Raw server/cache data. The UI-facing `employees`, `attendance`, `breaks` are derived
    // below by replaying the persisted offline queue on top of these.
    const [serverEmployees, setEmployees] = useState<Employee[]>([]);
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [serverAttendance, setAttendance] = useState<Attendance[]>([]);
    const [serverBreaks, setBreaks] = useState<Break[]>([]);
    const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [logbookEntries, setLogbookEntries] = useState<LogbookEntry[]>([]);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const hasLoadedNotificationsRef = useRef(false);
    const notifiedIdsRef = useRef<Set<string>>(new Set());
    const isCheckingRef = useRef(false);
    const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
    const [salesRecords, setSalesRecords] = useState<SalesRecord[]>([]);
    const [swapRequests, setSwapRequests] = useState<SwapRequest[]>([]);
    const [activeBroadcast, setActiveBroadcast] = useState<{ message: string, priority: 'normal' | 'emergency', createdAt: string } | null>(null);

    // Config State
    const [settings, setSettings] = useState<AppSettings>(defaultSettings);
    const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(defaultNotificationSettings);
    const [manualSubscription, setManualSubscription] = useState<Subscription | null>(null);
    const [stripeSubscription, setStripeSubscription] = useState<Subscription | null>(null);
    const [companyName, setCompanyName] = useState('KithHR');
    const [companyLogo, setCompanyLogo] = useState<string | null>(null);
    const [managerPin, setManagerPin] = useState('1234');
    const [managerPhone, setManagerPhone] = useState('');
    const [calendarToken, setCalendarToken] = useState<string | null>(null);

    // Offline-aware view: server data + queued offline actions (persists across restarts)
    const { employees, attendance, breaks } = React.useMemo(
        () => applyPendingActions(serverEmployees, serverAttendance, serverBreaks, pendingActions),
        [serverEmployees, serverAttendance, serverBreaks, pendingActions]
    );

    // Helper to reset all data state (for logout/isolation)
    const resetState = useCallback(() => {
        setEmployees([]);
        setShifts([]);
        setAttendance([]);
        setBreaks([]);
        setTasks([]);
        setLogbookEntries([]);
        setNotifications([]);
        setAuditLog([]);
        setSalesRecords([]);
        setSwapRequests([]);
        setCalendarToken(null);
        setActiveBroadcast(null);
        setSettings(defaultSettings);
        setNotificationSettings(defaultNotificationSettings);
        setManualSubscription(null);
        setStripeSubscription(null);
        setCompanyName('KithHR');
        setCompanyLogo(null);
        setManagerPin('1234');
        setManagerPhone('');
        setIsLoading(false);
        // NOTE: the offline queue is intentionally NOT cleared here — it is persisted
        // in IndexedDB and must survive restarts until it is synced.
    }, []);

    // App State
    const [isLoading, setIsLoading] = useState(true);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const pendingSyncCount = pendingActions.length;
    const refreshPendingActions = useCallback(async () => {
        try {
            setPendingActions(await getPendingActions());
        } catch (e) {
            console.error('Failed to read offline queue', e);
        }
    }, []);
    const [lastSyncTime, setLastSyncTime] = useState<string | null>(localStorage.getItem('lastSyncTime'));

    // Computed Properties
    // Computed Subscription Logic: Stripe always overrides manual profile state if active/canceled
    const subscription = React.useMemo(() => {
        // Special override for administrator account
        if (currentUser?.email === 'd0bl3@abv.bg') {
            return {
                ...(stripeSubscription || manualSubscription || {}),
                status: 'pro',
                employeeLimit: 999,
                cancelAtPeriodEnd: false, // Hide "Ending in X days"
                trialEnds: undefined,      // Hide trial countdowns
                renewalDate: ''            // Hide renewal badges
            } as Subscription;
        }

        if (stripeSubscription) return stripeSubscription;
        return manualSubscription;
    }, [stripeSubscription, manualSubscription, currentUser]);

    const isTrialExpired = React.useMemo(() => {
        // Special override for administrator account
        if (currentUser?.email === 'd0bl3@abv.bg') return false;

        // Teams up to 15 employees are Free Forever and never expire!
        if (employees.length <= 15) {
            return false;
        }

        // Teams with > 15 employees require an active Pro subscription:
        // 1. Trial status check
        if (subscription?.status === 'trial') {
            if (!subscription.trialEnds) return true;
            return new Date(subscription.trialEnds) < new Date();
        }

        // 2. Inactive or unpaid subscription
        if (subscription?.status === 'inactive') {
            return true;
        }

        // 3. Subscription canceled and billing period has ended
        if (subscription?.cancelAtPeriodEnd && subscription?.renewalDate) {
            if (new Date(subscription.renewalDate) < new Date()) {
                return true;
            }
        }

        // 4. No paid subscription on record for a team with > 15 employees
        if (!subscription || subscription.status === 'free') {
            return true;
        }

        return false;
    }, [subscription, currentUser, employees.length]);

    const isPro = React.useMemo(() => {
        // Special override for administrator account - must be first
        if (currentUser?.email === 'd0bl3@abv.bg') return true;

        if (subscription?.status === 'custom') return true;
        if (subscription?.status === 'pro') return true;
        if (subscription?.status === 'standard') return true; // MAP LEGACY TO PRO AS REQUESTED
        
        // PRO ALL-IN-ONE: Teams with 1 to 15 employees get full PRO features included for FREE!
        if (employees.length <= 15) return true;
        
        // Trial users get Pro ONLY if their trial has not expired
        if (subscription?.status === 'trial' && !isTrialExpired) return true;
        
        // Grace period for recently downgraded accounts
        if (subscription?.gracePeriodUntil) {
            return new Date(subscription.gracePeriodUntil) > new Date();
        }
        return false;
    }, [subscription, currentUser, isTrialExpired, employees.length]);

    const employeeLimit = React.useMemo(() => {
        if (currentUser?.email === 'd0bl3@abv.bg') return 999;
        const stored = subscription?.employeeLimit || 15;
        // Free tier guarantees up to 15 employee seats
        if (!subscription?.stripeSubscriptionId || subscription?.status === 'free') {
            return Math.max(stored, 15);
        }
        return stored;
    }, [subscription, currentUser]);

    const canAddEmployee = employees.length < employeeLimit;

// isTrialExpired is now declared above isPro

    const kpiData = React.useMemo(() => calculateKPIData(employees, attendance, breaks, shifts, settings), [employees, attendance, breaks, shifts, settings]);

    // Sync Logic Helpers
    const getBasePath = useCallback(() => {
        if (currentUser) return `managers/${currentUser.uid}`;
        if (employeeUser) return `managers/${employeeUser.managerId}`;
        
        // Kiosk Mode: Use the paired manager ID from local storage
        const pairedManagerId = localStorage.getItem('kithhr_manager_id');
        if (pairedManagerId) return `managers/${pairedManagerId}`;
        
        return null;
    }, [currentUser, employeeUser]);


    // Load the persisted offline queue on startup so queued clock/break actions
    // are immediately reflected in the UI (network listeners live further below).
    useEffect(() => {
        refreshPendingActions();
    }, [refreshPendingActions]);

    // Firestore Subscriptions
    useEffect(() => {
        const basePath = getBasePath();
        if (!basePath) {
            resetState();
            setIsLoading(false);
            return;
        }

        let isUnmounted = false;
        let unsubs: (() => void)[] = [];

        setIsLoading(true);
        resetState(); // Reset before fetching new data

        initFirebase().then(() => {
            if (isUnmounted) return;
            let profileSubscription: Subscription | null = null;

            // Company Profile
            const unsubProfile = onSnapshot(doc(db, basePath, 'company', 'profile'), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                
                // Only update state if values actually change to break loops
                if (data.name !== companyName) setCompanyName(data.name || 'KithHR');
                if (data.logo !== companyLogo) setCompanyLogo(data.logo || null);
                if (data.managerPin !== managerPin) setManagerPin(data.managerPin || '1234');
                if ((data.phone || data.mobile || '') !== managerPhone) setManagerPhone(data.phone || data.mobile || '');
                if (data.calendarToken !== calendarToken) setCalendarToken(data.calendarToken || null);
                
                // Deep merge settings with defaults to ensure new fields (like Xero) are present
                const mergedSettings = { ...defaultSettings, ...(data.settings || {}) };
                
                // Only update state if the settings actually changed to prevent render loops
                setSettings(prev => {
                    if (JSON.stringify(prev) === JSON.stringify(mergedSettings)) return prev;
                    return mergedSettings;
                });

                setNotificationSettings(data.notificationSettings || defaultNotificationSettings);

                // Set default trial subscription from profile if no Stripe subscription overrides it later
                profileSubscription = data.subscription;

                // ADMIN OVERRIDE: Ensure d0bl3@abv.bg always has 'pro' status in Firestore.
                // This only runs for this specific account and does not affect any other users.
                if (currentUser?.email === 'd0bl3@abv.bg' && profileSubscription?.status !== 'pro' && profileSubscription?.status !== 'custom') {
                    profileSubscription = { status: 'pro', employeeLimit: 999 };
                    updateDoc(doc(db, basePath, 'company', 'profile'), { subscription: profileSubscription });
                }

                // Self-healing: Ensure trial exists if it was missed during registration (15 free seats)
                if (currentUser?.email !== 'd0bl3@abv.bg' && (!profileSubscription || (profileSubscription.status === 'trial' && !profileSubscription.trialEnds))) {
                    profileSubscription = { 
                        status: 'trial', 
                        trialEnds: new Date(Date.now() + 12096e5).toISOString(), 
                        employeeLimit: 15 
                    };
                    updateDoc(doc(db, basePath, 'company', 'profile'), { subscription: profileSubscription });
                }

                setManualSubscription(profileSubscription);

            } else {
                // Initialize if not exists (15 free seats)
                const initialTrial = { 
                    status: 'trial', 
                    trialEnds: new Date(Date.now() + 12096e5).toISOString(), 
                    employeeLimit: 15 
                };
                setDoc(doc(db, basePath, 'company', 'profile'), {
                    name: 'KithHR',
                    managerPin: '1234',
                    settings: defaultSettings,
                    notificationSettings: defaultNotificationSettings,
                    subscription: initialTrial
                });
                setManualSubscription(initialTrial);
            }
            setIsLoading(false);
        }, (error) => {
            console.error("Profile Snapshot Error:", error);
            setIsLoading(false);
        });

        const unsubEmployees = onSnapshot(collection(db, basePath, 'employees'), (snapshot) => {
            setEmployees(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Employee)));
        });

        const unsubShifts = onSnapshot(collection(db, basePath, 'shifts'), (snapshot) => {
            setShifts(snapshot.docs.map(d => {
                const data = d.data();
                // Convert timestamps to ISO strings if needed
                const start = data.start?.toDate?.()?.toISOString() || data.start;
                const end = data.end?.toDate?.()?.toISOString() || data.end;
                return { id: d.id, ...data, start, end } as Shift;
            }));
        });

        const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
        const unsubAttendance = onSnapshot(query(collection(db, basePath, 'attendance'), where('clockIn', '>=', fourteenDaysAgo)), (snapshot) => {
            const fetchedAttendance = snapshot.docs.map(d => {
                const data = d.data();
                return {
                    ...data,
                    id: d.id,
                    clockIn: data.clockIn?.toDate ? data.clockIn.toDate().toISOString() : data.clockIn,
                    clockOut: data.clockOut?.toDate ? data.clockOut.toDate().toISOString() : data.clockOut
                } as Attendance;
            });
            setAttendance(fetchedAttendance);
            
            // Self-Healing Status Logic:
            // If an employee has an active attendance session, their status MUST be 'Clocked In' or 'On Break'.
            // If the employee doc says 'Clocked Out', it means the database is slightly out of sync.
            setEmployees(prev => prev.map(emp => {
                const activeSession = fetchedAttendance.find(a => a.employeeId === emp.id && a.status === 'active');
                if (activeSession && emp.status === 'Clocked Out') {
                    return { ...emp, status: 'Clocked In' };
                }
                return emp;
            }));
        });

        const unsubBreaks = onSnapshot(collection(db, basePath, 'breaks'), (snapshot) => {
            setBreaks(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Break)));
        });

        const unsubTasks = onSnapshot(collection(db, basePath, 'tasks'), (snapshot) => {
            setTasks(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Task)));
        });

        const unsubLogbook = onSnapshot(collection(db, basePath, 'logbook'), (snapshot) => {
            setLogbookEntries(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as LogbookEntry)));
        });

        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const unsubNotifications = onSnapshot(query(
            collection(db, basePath, 'notifications'),
            where('timestamp', '>=', thirtyDaysAgo)
        ), (snapshot) => {
            const fetchedNotifications = snapshot.docs.map(d => {
                const data = d.data();
                // Ensure timestamp is an ISO string
                const timestamp = data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : data.timestamp;
                return { id: d.id, ...data, timestamp } as Notification;
            });
            
            setNotifications(fetchedNotifications.sort((a, b) => 
                new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            ));
            hasLoadedNotificationsRef.current = true;
        });

        // Listen for Stripe Extension Subscriptions
        const unsubStripeSubscriptions = onSnapshot(collection(db, basePath, 'subscriptions'), (snapshot) => {
            if (snapshot.empty) {
                // If no Stripe subscriptions exist, let the profile snapshot handle the trial status
                return;
            }

            // Get all sub docs and sort by metadata.created or similar if available, or just use Firestore's natural order
            // Actually, the extension docs usually have a 'created' field.
            const sortedSubs = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() } as any))
                .sort((a, b) => (b.created?.seconds || 0) - (a.created?.seconds || 0));

            const nowSeconds = Math.floor(Date.now() / 1000);

            // Find the most relevant subscription:
            // 1. Actively running subscriptions (active or trialing)
            // 2. Past due (card payment retry phase)
            // 3. Canceled subscriptions that are still within their paid period
            // 4. Any recent subscription record
            const activeSub = sortedSubs.find(s => s.status === 'active' || s.status === 'trialing')
                || sortedSubs.find(s => s.status === 'past_due')
                || sortedSubs.find(s => s.status === 'canceled' && s.current_period_end?.seconds > nowSeconds)
                || sortedSubs.find(s => s.status === 'unpaid')
                || sortedSubs[0];

            if (activeSub) {
                const items = activeSub.items;
                let totalSeats = 15;
                let planStatus: 'standard' | 'pro' | 'trial' | 'inactive' = 'pro';

                if (items && items.length > 0) {
                    totalSeats = items[0].quantity || 15;
                }

                const isPeriodEnded = activeSub.current_period_end ? activeSub.current_period_end.seconds <= nowSeconds : false;

                if (activeSub.status === 'trialing') {
                    planStatus = 'trial';
                } else if (activeSub.status === 'unpaid' || (activeSub.status === 'canceled' && isPeriodEnded)) {
                    planStatus = 'inactive';
                } else {
                    planStatus = 'pro';
                }

                // ADMIN OVERRIDE: If this is the admin, force Pro status even if Stripe says Standard
                if (currentUser?.email === 'd0bl3@abv.bg') {
                    planStatus = 'pro';
                    totalSeats = 999;
                }

                setStripeSubscription({
                    status: activeSub.status === 'trialing' ? 'trial' : planStatus,
                    employeeLimit: totalSeats,
                    cancelAtPeriodEnd: activeSub.cancel_at_period_end || false,
                    renewalDate: activeSub.current_period_end ? new Date(activeSub.current_period_end.seconds * 1000).toISOString() : '',
                    trialEnds: activeSub.trial_end ? new Date(activeSub.trial_end.seconds * 1000).toISOString() : undefined,
                    stripeSubscriptionId: activeSub.id,
                    stripeCustomerId: activeSub.customer,
                    billingCycle: activeSub.items && activeSub.items.length > 0 && activeSub.items[0].price?.recurring?.interval === 'year' ? 'yearly' : 'monthly'
                });
            } else {
                setStripeSubscription(null);
            }
        });

        const unsubAuditLog = onSnapshot(query(collection(db, basePath, 'auditLog'), orderBy('timestamp', 'desc'), limit(100)), (snapshot) => {
            setAuditLog(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as AuditLogEntry)));
        });

        const unsubSales = onSnapshot(collection(db, basePath, 'sales'), (snapshot) => {
            setSalesRecords(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as SalesRecord)));
        });

        const unsubSwapRequests = onSnapshot(collection(db, basePath, 'swapRequests'), (snapshot) => {
            setSwapRequests(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as SwapRequest)));
        });

        const unsubAnnouncements = onSnapshot(doc(db, basePath, 'announcements', 'current'), (docSnap) => {
            if (docSnap.exists()) {
                setActiveBroadcast(docSnap.data() as any);
            } else {
                setActiveBroadcast(null);
            }
        }, (error) => {
            console.error("Announcements Snapshot Error:", error);
        });

            unsubs.push(unsubProfile, unsubEmployees, unsubShifts, unsubAttendance, unsubBreaks, unsubTasks, unsubLogbook, unsubNotifications, unsubStripeSubscriptions, unsubAuditLog, unsubSales, unsubSwapRequests, unsubAnnouncements);
        });

        return () => {
            isUnmounted = true;
            unsubs.forEach(unsub => unsub && unsub());
        };
    }, [currentUser, employeeUser, getBasePath]);

    // Periodic checks for all notification types
    useEffect(() => {
        const runChecks = async () => {
            if (isCheckingRef.current) return;
            isCheckingRef.current = true;
            try {
                const basePath = getBasePath();
                if (!basePath || !isOnline || !hasLoadedNotificationsRef.current) return;

                const now = new Date();

            // 1. Missed Clock-outs
            if (notificationSettings.missedClockOuts) {
                const thresholdHours = notificationSettings.missedClockOutHours || 12;
                const activeAttendance = attendance.filter(a => a.status === 'active');
                
                for (const att of activeAttendance) {
                    const clockInTime = new Date(att.clockIn);
                    const hoursSinceClockIn = (now.getTime() - clockInTime.getTime()) / (1000 * 60 * 60);

                    if (hoursSinceClockIn > thresholdHours) {
                        const alreadyNotified = notifications.some(n => 
                            n.type === 'missed_clock_out' && n.relatedId === att.id
                        ) || notifiedIdsRef.current.has(`missed_clock_out_${att.id}`);

                        if (!alreadyNotified) {
                            const emp = employees.find(e => e.id === att.employeeId);
                            notifiedIdsRef.current.add(`missed_clock_out_${att.id}`);
                            const notifDocRef = doc(db, basePath, 'notifications', `missed_clock_out_${att.id}`);
                            await setDoc(notifDocRef, {
                                id: notifDocRef.id,
                                title: '⚠️ Missed Clock-Out Alert',
                                message: `${emp?.firstName || 'Employee'} appears to have a missed clock-out (Over ${thresholdHours}h).`,
                                type: 'missed_clock_out',
                                timestamp: now.toISOString(),
                                isRead: false,
                                isDismissed: false,
                                employeeId: att.employeeId,
                                relatedId: att.id,
                                recipient: 'manager',
                                recipientRole: 'manager'
                            }, { merge: true });
                        }
                    }
                }
            }

            // 2. Upcoming Shift Reminders (1h before)
            if (notificationSettings.upcomingShifts) {
                const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
                const upcomingShifts = shifts.filter(s => {
                    const shiftStart = new Date(s.start);
                    return shiftStart > now && shiftStart <= oneHourLater && s.type === 'work' && s.status !== 'pending';
                });

                for (const shift of upcomingShifts) {
                    const alreadyNotified = notifications.some(n => 
                        n.type === 'shift_reminder' && n.relatedId === shift.id
                    ) || notifiedIdsRef.current.has(`shift_reminder_${shift.id}`);

                    if (!alreadyNotified) {
                        const emp = employees.find(e => e.id === shift.employeeId);
                        notifiedIdsRef.current.add(`shift_reminder_${shift.id}`);
                        const notifDocRef = doc(db, basePath, 'notifications', `shift_reminder_${shift.id}`);
                        await setDoc(notifDocRef, {
                            id: notifDocRef.id,
                            title: '⏰ Shift Reminder',
                            message: `Upcoming shift for ${emp?.firstName || 'Employee'} at ${new Date(shift.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`,
                            type: 'shift_reminder',
                            timestamp: now.toISOString(),
                            isRead: false,
                            isDismissed: false,
                            employeeId: shift.employeeId,
                            relatedId: shift.id,
                            recipient: 'employee',
                            recipientRole: 'employee'
                        }, { merge: true });
                    }
                }
            }

            // 3. Overdue Task Alerts
            if (notificationSettings.overdueTasks) {
                const overdueTasks = tasks.filter(t => t.status === 'pending' && t.dueDate && new Date(t.dueDate) < now);

                for (const task of overdueTasks) {
                    const alreadyNotified = notifications.some(n => 
                        n.type === 'overdue_task' && n.relatedId === task.id
                    ) || notifiedIdsRef.current.has(`overdue_task_${task.id}`);

                    if (!alreadyNotified) {
                        notifiedIdsRef.current.add(`overdue_task_${task.id}`);
                        const notifDocRef = doc(db, basePath, 'notifications', `overdue_task_${task.id}`);
                        await setDoc(notifDocRef, {
                            id: notifDocRef.id,
                            title: '📋 Overdue Task Alert',
                            message: `Task overdue: "${task.title}".`,
                            type: 'overdue_task',
                            timestamp: now.toISOString(),
                            isRead: false,
                            isDismissed: false,
                            relatedId: task.id,
                            recipient: 'manager',
                            recipientRole: 'manager'
                        }, { merge: true });
                    }
                }
            }

            // 4. Sickness Pattern Alerts (3+ days in 30 days) - Alert Manager ONLY
            if (notificationSettings.sicknessAlerts) {
                const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                const recentSickShifts = shifts.filter(s => s.type === 'sick' && new Date(s.start) >= thirtyDaysAgo);

                // Group by employee
                const employeeSickCounts = new Map<string, number>();
                recentSickShifts.forEach(s => {
                    employeeSickCounts.set(s.employeeId, (employeeSickCounts.get(s.employeeId) || 0) + 1);
                });

                // Calculate ISO week bucket (e.g. 2026_w42) so the pattern alerts once per week maximum
                const oneJan = new Date(now.getFullYear(), 0, 1);
                const numberOfDays = Math.floor((now.getTime() - oneJan.getTime()) / (24 * 60 * 60 * 1000));
                const weekNumber = Math.ceil((now.getDay() + 1 + numberOfDays) / 7);
                const currentWeekBucket = `${now.getFullYear()}_w${weekNumber}`;

                for (const [employeeId, count] of employeeSickCounts.entries()) {
                    if (count >= 3) {
                        const sicknessKey = `sickness_${employeeId}_${currentWeekBucket}`;
                        // Notify if not already notified for this pattern in the last 7 days
                        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                        const alreadyNotified = notifications.some(n => 
                            n.type === 'sickness_pattern' && 
                            n.employeeId === employeeId && 
                            new Date(n.timestamp) >= sevenDaysAgo
                        ) || notifiedIdsRef.current.has(sicknessKey);

                        if (!alreadyNotified) {
                            const emp = employees.find(e => e.id === employeeId);
                            notifiedIdsRef.current.add(sicknessKey);
                            const notifDocRef = doc(db, basePath, 'notifications', sicknessKey);
                            await setDoc(notifDocRef, {
                                id: notifDocRef.id,
                                title: '⚠️ Sickness Pattern Detected',
                                message: `Sickness pattern detected for ${emp?.firstName || 'Employee'}: ${count} days in last 30 days.`,
                                type: 'sickness_pattern',
                                timestamp: now.toISOString(),
                                isRead: false,
                                isDismissed: false,
                                employeeId: employeeId,
                                recipient: 'manager',
                                recipientRole: 'manager'
                            }, { merge: true });
                        }
                    }
                }
            }

            // 5. Late Arrival Alerts
            if (notificationSettings.lateArrivals) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                // Find all attendance records from today that are NOT yet completed
                const todaysAttendance = attendance.filter(a => 
                    new Date(a.clockIn) >= today && a.status !== 'completed'
                );

                for (const att of todaysAttendance) {
                    const clockInTime = new Date(att.clockIn);
                    const todayStr = clockInTime.toDateString();
                    const scheduledShift = shifts.find(s => 
                        s.employeeId === att.employeeId && 
                        s.type === 'work' && 
                        new Date(s.start).toDateString() === todayStr
                    );

                    if (scheduledShift) {
                        const shiftStart = new Date(scheduledShift.start);
                        const graceMinutes = settings?.gracePeriod !== undefined ? settings.gracePeriod : 5;
                        const gracePeriodMs = graceMinutes * 60 * 1000;
                        
                        if (clockInTime.getTime() > (shiftStart.getTime() + gracePeriodMs)) {
                            // Only notify once per attendance record (by relatedId)
                            const alreadyNotified = notifications.some(n => 
                                n.type === 'late_arrival' && n.relatedId === att.id
                            ) || notifiedIdsRef.current.has(`late_arrival_${att.id}`);

                            if (!alreadyNotified) {
                                const emp = employees.find(e => e.id === att.employeeId);
                                notifiedIdsRef.current.add(`late_arrival_${att.id}`);
                                const minsLate = Math.round((clockInTime.getTime() - shiftStart.getTime()) / (1000 * 60));
                                const notifDocRef = doc(db, basePath, 'notifications', `late_arrival_${att.id}`);
                                await setDoc(notifDocRef, {
                                    id: notifDocRef.id,
                                    title: '⚠️ Late Arrival Alert',
                                    message: `${emp?.firstName || 'Employee'} arrived late (${minsLate} mins late).`,
                                    type: 'late_arrival',
                                    timestamp: now.toISOString(),
                                    isRead: false,
                                    isDismissed: false,
                                    employeeId: att.employeeId,
                                    relatedId: att.id,
                                    recipient: 'manager',
                                    recipientRole: 'manager'
                                }, { merge: true });
                            }
                        }
                    }
                }
            }

            // 6. Qualification Expiry Alerts (Check every day, but 5m interval is fine)
            if (notificationSettings.qualificationExpiry) {
                const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
                
                for (const emp of employees) {
                    const qualifications = [
                        { name: 'Driver License', date: emp.driversLicenseValidUntil },
                        { name: 'CPC Card', date: emp.cpcCardValidUntil },
                        { name: 'Digital Card', date: emp.digitalCardValidUntil }
                    ];

                    for (const qual of qualifications) {
                        if (qual.date) {
                            const expDate = new Date(qual.date);
                            if (expDate < thirtyDaysFromNow) {
                                // Only notify once per week per qualification to avoid spam
                                const qualKey = `qual_${emp.id}_${qual.name.replace(/\s+/g, '_')}`;
                                const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                                const alreadyNotified = notifications.some(n => 
                                    n.type === 'qualification_expiry' && 
                                    n.employeeId === emp.id && 
                                    n.message.includes(qual.name) &&
                                    new Date(n.timestamp) >= sevenDaysAgo
                               ) || notifiedIdsRef.current.has(qualKey);

                                if (!alreadyNotified) {
                                    const isExpired = expDate < now;
                                    const msg = `${qual.name} for ${emp.firstName} ${isExpired ? 'has expired' : 'expires soon'} (${new Date(qual.date).toLocaleDateString()}).`;
                                    notifiedIdsRef.current.add(qualKey);
                                    const notifDocRef = doc(db, basePath, 'notifications', qualKey);
                                    await setDoc(notifDocRef, {
                                        id: notifDocRef.id,
                                        title: '📜 Qualification Expiry Alert',
                                        message: msg,
                                        type: 'qualification_expiry',
                                        timestamp: now.toISOString(),
                                        isRead: false,
                                        isDismissed: false,
                                        employeeId: emp.id,
                                        recipient: 'manager',
                                        recipientRole: 'manager'
                                    }, { merge: true });
                                }
                            }
                        }
                    }
                }
            }

            // 7. Excessive Break Alerts
            if (notificationSettings.excessiveBreaks) {
                const activeBreaks = breaks.filter(b => !b.end);
                
                for (const brk of activeBreaks) {
                    const att = attendance.find(a => a.id === brk.attendanceId);
                    if (att) {
                        const emp = employees.find(e => e.id === att.employeeId);
                        if (emp && emp.maxBreakTime) {
                            const breakStart = new Date(brk.start);
                            const breakDurationMins = (now.getTime() - breakStart.getTime()) / (1000 * 60);

                            if (breakDurationMins > emp.maxBreakTime) {
                                // Only notify if not already notified for this specific break session
                                const alreadyNotified = notifications.some(n => 
                                    n.type === 'excessive_break' && n.relatedId === brk.id
                                ) || notifiedIdsRef.current.has(`excessive_break_${brk.id}`);

                                if (!alreadyNotified) {
                                    notifiedIdsRef.current.add(`excessive_break_${brk.id}`);
                                    const notifDocRef = doc(db, basePath, 'notifications', `excessive_break_${brk.id}`);
                                    await setDoc(notifDocRef, {
                                        id: notifDocRef.id,
                                        title: '☕ Excessive Break Alert',
                                        message: `${emp.firstName} has been on break for ${Math.round(breakDurationMins)} minutes (Limit: ${emp.maxBreakTime}m).`,
                                        type: 'excessive_break',
                                        timestamp: now.toISOString(),
                                        isRead: false,
                                        isDismissed: false,
                                        employeeId: emp.id,
                                        relatedId: brk.id,
                                        recipient: 'manager',
                                        recipientRole: 'manager'
                                    }, { merge: true });
                                }
                            }
                        }
                    }
                }
            }
            } finally {
                isCheckingRef.current = false;
            }
        };

        const interval = setInterval(runChecks, 5 * 60 * 1000); // Check every 5 minutes
        runChecks(); // Initial check

        return () => clearInterval(interval);
    }, [attendance, shifts, tasks, notificationSettings, notifications, employees, isOnline]);

    // Actions
    const addEmployee = async (employeeData: Omit<Employee, 'id'>) => {
        if (isTrialExpired) throw new Error("Trial expired. Please upgrade to add more employees.");
        if (!canAddEmployee) throw new Error("Employee limit reached.");
        const basePath = getBasePath();
        if (!basePath) return;
        const empCollection = collection(db, basePath, 'employees');
        const empDocRef = doc(empCollection);
        await setDoc(empDocRef, removeUndefined({
            ...employeeData,
            id: empDocRef.id
        }));
    };

    const updateEmployee = async (employee: Employee) => {
        const basePath = getBasePath();
        if (!basePath) return;
        await updateDoc(doc(db, basePath, 'employees', employee.id), removeUndefined(employee));
    };

    const deleteEmployee = async (id: string) => {
        const basePath = getBasePath();
        if (!basePath) return;
        await deleteDoc(doc(db, basePath, 'employees', id));
    };

    const importEmployees = async (csvString: string) => {
        const basePath = getBasePath();
        if (!basePath) return { successCount: 0, errorCount: 0, errors: [] };

        return new Promise<{ successCount: number; errorCount: number; errors: string[] }>((resolve) => {
            Papa.parse(csvString, {
                header: true,
                skipEmptyLines: true,
                complete: async (results) => {
                    let successCount = 0;
                    let errorCount = 0;
                    const errors: string[] = [];
                    const batch = writeBatch(db);

                    // Check limit
                    const remainingSlots = employeeLimit - employees.length;
                    const rowsToProcess = results.data.slice(0, remainingSlots);
                    if (results.data.length > remainingSlots) {
                        errors.push(`File contains ${results.data.length} records, but you only have ${remainingSlots} slots remaining. Only first ${remainingSlots} processed.`);
                    }

                    for (const row of rowsToProcess as any[]) {
                        try {
                            if (!row['First Name'] || !row['Last Name']) throw new Error("Missing name");

                            const newEmpRef = doc(collection(db, basePath, 'employees'));
                            const empData = {
                                firstName: row['First Name'],
                                lastName: row['Last Name'],
                                email: row['Email'] || '',
                                position: row['Position'] || 'Staff',
                                department: row['Department'] || 'General',
                                hourlyRate: parseFloat(row['Hourly Rate']) || 0,
                                pin: row['PIN'] || '0000',
                                status: 'Clocked Out',
                                holidaysAvailable: parseInt(row['Holidays Available']) || 20,
                                timezone: row['Timezone'] || settings.timezone,
                                phone: row['Phone'] || '',
                                address: row['Address'] || '',
                                hireDate: row['Hire Date'] || new Date().toISOString(),
                                avatarUrl: '',
                            };
                            batch.set(newEmpRef, removeUndefined(empData));
                            successCount++;
                        } catch (e: any) {
                            errorCount++;
                            errors.push(`Row error: ${e.message}`);
                        }
                    }

                    if (successCount > 0) await batch.commit();
                    resolve({ successCount, errorCount, errors });
                }
            });
        });
    };

    const exportEmployees = (employeesToExport: Employee[]) => {
        const data = employeesToExport.map(e => ({
            "Payroll ID": e.payrollId || '',
            "First Name": e.firstName,
            "Last Name": e.lastName,
            "Email": e.email,
            "Phone": e.phone || '',
            "Department": e.department,
            "Position": e.position,
            "Status": e.status,
            "Hourly Rate": e.hourlyRate,
            "PIN": e.pin,
            "Holidays Available": e.holidaysAvailable,
            "Starting Balance": e.holidayStartingBalance || '',
            "Hire Date": e.hireDate ? new Date(e.hireDate).toISOString().split('T')[0] : '',
            "Address": e.address || '',
            "NI Number": e.niNumber || '',
            "Tax Code": e.taxCode || '',
            "DOB": e.dateOfBirth ? new Date(e.dateOfBirth).toISOString().split('T')[0] : '',
            "License Expiry": e.driversLicenseValidUntil ? new Date(e.driversLicenseValidUntil).toISOString().split('T')[0] : '',
            "CPC Expiry": e.cpcCardValidUntil ? new Date(e.cpcCardValidUntil).toISOString().split('T')[0] : '',
            "Digi Card Expiry": e.digitalCardValidUntil ? new Date(e.digitalCardValidUntil).toISOString().split('T')[0] : '',
        }));

        const csv = Papa.unparse(data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const dateStr = new Date().toISOString().split('T')[0];
        link.download = `Employees_Export_${dateStr}.csv`;
        link.click();
    };

    const addShift = async (shiftData: any, recurrenceRule: string, recurrenceEndDate?: string) => {
        if (isTrialExpired) throw new Error("Trial expired. Please upgrade to schedule shifts.");
        const basePath = getBasePath();
        if (!basePath) return;

        const batch = writeBatch(db);

        // Calculate potential holiday deduction if it's a holiday type
        let totalHolidayDaysToDeduct = 0;
        let employee: Employee | undefined;

        if (shiftData.type === 'holiday') {
            // We need the employee's availability to calculate accurate working days
            const empDoc = await getDoc(doc(db, basePath, 'employees', shiftData.employeeId));
            if (empDoc.exists()) {
                employee = empDoc.data() as Employee;
            }
        }

        // Initial shift
        const newShiftRef = doc(collection(db, basePath, 'shifts'));
        const groupId = recurrenceRule !== 'none' ? newShiftRef.id : undefined; // Use first shift ID as group ID

        const baseShift = {
            ...shiftData,
            // If approval is required, default to pending for work shifts
            status: (settings.shiftApprovalRequired && shiftData.type === 'work') ? 'pending' : 'approved',
            start: Timestamp.fromDate(new Date(shiftData.start)),
            end: Timestamp.fromDate(new Date(shiftData.end)),
        };

        if (shiftData.type === 'holiday' && employee) {
            totalHolidayDaysToDeduct += calculateWorkingDays(new Date(shiftData.start), new Date(shiftData.end), employee.availability);
        }

        // Only add recurrence if it exists (not 'none') to prevent invalid data error
        if (recurrenceRule !== 'none') {
            baseShift.recurrence = { rule: recurrenceRule, endDate: recurrenceEndDate, groupId };
        }

        batch.set(newShiftRef, baseShift);

        // Auto-create attendance for manual sick/holiday
        if (shiftData.type === 'sick' || shiftData.type === 'holiday') {
            createAttendanceSync(batch, basePath, newShiftRef.id, shiftData.employeeId, shiftData.type, new Date(shiftData.start), new Date(shiftData.end), employee?.availability);
        }

        if (recurrenceRule !== 'none' && recurrenceEndDate) {
            const start = new Date(shiftData.start);
            const end = new Date(shiftData.end);
            const until = new Date(recurrenceEndDate);
            let currentStart = new Date(start);
            let currentEnd = new Date(end);

            // Limit to 1 year of shifts to prevent infinite loops/excessive writes
            const safetyLimit = new Date(start);
            safetyLimit.setFullYear(safetyLimit.getFullYear() + 1);
            const finalDate = until < safetyLimit ? until : safetyLimit;

            while (true) {
                if (recurrenceRule === 'daily') currentStart.setDate(currentStart.getDate() + 1);
                else if (recurrenceRule === 'weekly') currentStart.setDate(currentStart.getDate() + 7);
                else if (recurrenceRule === 'monthly') currentStart.setMonth(currentStart.getMonth() + 1);

                if (currentStart > finalDate) break;

                const duration = end.getTime() - start.getTime();
                currentEnd = new Date(currentStart.getTime() + duration);

                const ref = doc(collection(db, basePath, 'shifts'));
                batch.set(ref, {
                    ...shiftData,
                    status: (settings.shiftApprovalRequired && shiftData.type === 'work') ? 'pending' : 'approved',
                    start: Timestamp.fromDate(new Date(currentStart)),
                    end: Timestamp.fromDate(new Date(currentEnd)),
                    recurrence: { rule: recurrenceRule, endDate: recurrenceEndDate, groupId }
                });

                // Auto-create attendance for manual sick/holiday (recurrence)
                if (shiftData.type === 'sick' || shiftData.type === 'holiday') {
                    createAttendanceSync(batch, basePath, ref.id, shiftData.employeeId, shiftData.type, new Date(currentStart), new Date(currentEnd), employee?.availability);
                }

                if (shiftData.type === 'holiday' && employee) {
                    totalHolidayDaysToDeduct += calculateWorkingDays(new Date(currentStart), new Date(currentEnd), employee.availability);
                }
            }
        }

        // Update employee holiday balance if needed
        if (totalHolidayDaysToDeduct > 0 && employee) {
            const empRef = doc(db, basePath, 'employees', employee.id);
            const newBalance = (employee.holidaysAvailable || 0) - totalHolidayDaysToDeduct;
            batch.update(empRef, { holidaysAvailable: newBalance });
        }

        await batch.commit();
    };

    const addShiftsBatch = async (shiftsData: any[]) => {
        if (isTrialExpired) throw new Error("Trial expired. Please upgrade to schedule shifts.");
        const basePath = getBasePath();
        if (!basePath) return;

        const CHUNK_SIZE = 450;
        for (let i = 0; i < shiftsData.length; i += CHUNK_SIZE) {
            const chunk = shiftsData.slice(i, i + CHUNK_SIZE);
            const batch = writeBatch(db);
            
            chunk.forEach(shiftData => {
                const newShiftRef = doc(collection(db, basePath, 'shifts'));
                const baseShift = {
                    ...shiftData,
                    status: (settings.shiftApprovalRequired && shiftData.type === 'work') ? 'pending' : 'approved',
                    start: Timestamp.fromDate(new Date(shiftData.start)),
                    end: Timestamp.fromDate(new Date(shiftData.end)),
                };
                batch.set(newShiftRef, baseShift);
                
                // Note: We're skipping complex auto-attendance and holiday deduction logic for batch paste 
                // to maintain performance and simplicity for now. 
                // Managers can always edit individual shifts if needed.
            });
            
            await batch.commit();
        }
    };

    const updateShift = async (shift: Shift) => {
        const basePath = getBasePath();
        if (!basePath) return;

        // Fetch attendance records to potentially delete/update outside transaction
        const attQuery = query(collection(db, basePath, 'attendance'), where('shiftId', '==', shift.id));
        const attSnapshot = await getDocs(attQuery);

        try {
            await runTransaction(db, async (transaction) => {
                const shiftRef = doc(db, basePath, 'shifts', shift.id);
                const shiftDoc = await transaction.get(shiftRef);
                if (!shiftDoc.exists()) throw new Error("Shift not found");

                const oldData = shiftDoc.data() as Shift;
                const newData = { ...shift };

                // Convert ISO strings back to Timestamps
                if (typeof newData.start === 'string') newData.start = Timestamp.fromDate(new Date(newData.start)) as any;
                if (typeof newData.end === 'string') newData.end = Timestamp.fromDate(new Date(newData.end)) as any;

                // Handle attendance records if type changed to/from sick/holiday
                const isNewSickHoliday = newData.type === 'sick' || newData.type === 'holiday';
                const isOldSickHoliday = oldData.type === 'sick' || oldData.type === 'holiday';

                const oldStartStr = (oldData.start as any)?.toDate ? (oldData.start as any).toDate().toISOString() : new Date(oldData.start as any).toISOString();
                const oldEndStr = (oldData.end as any)?.toDate ? (oldData.end as any).toDate().toISOString() : new Date(oldData.end as any).toISOString();
                const newStartStr = new Date(shift.start as any).toISOString();
                const newEndStr = new Date(shift.end as any).toISOString();

                if (isNewSickHoliday && !isOldSickHoliday) {
                    // Changed TO sick/holiday: Create attendance records
                    const empRef = doc(db, basePath, 'employees', newData.employeeId);
                    const empDoc = await transaction.get(empRef);
                    const empData = empDoc.exists() ? empDoc.data() as Employee : undefined;
                    
                    const start = (newData.start as any).toDate ? (newData.start as any).toDate() : new Date(newData.start as any);
                    const end = (newData.end as any).toDate ? (newData.end as any).toDate() : new Date(newData.end as any);
                    
                    createAttendanceSync(transaction, basePath, shift.id, newData.employeeId, newData.type, start, end, empData?.availability);
                } else if (!isNewSickHoliday && isOldSickHoliday) {
                    // Changed FROM sick/holiday: Delete existing auto-created attendance records
                    attSnapshot.docs.forEach(d => transaction.delete(d.ref));
                } else if (isNewSickHoliday && isOldSickHoliday) {
                    // Potentially updated dates/times for an existing sick/holiday shift
                    if (oldStartStr !== newStartStr || oldEndStr !== newEndStr || oldData.type !== newData.type) {
                        // Delete old ones
                        attSnapshot.docs.forEach(d => transaction.delete(d.ref));

                        // Create new ones
                        const empRef = doc(db, basePath, 'employees', newData.employeeId);
                        const empDoc = await transaction.get(empRef);
                        const empData = empDoc.exists() ? empDoc.data() as Employee : undefined;
                        
                        const start = (newData.start as any).toDate ? (newData.start as any).toDate() : new Date(newData.start as any);
                        const end = (newData.end as any).toDate ? (newData.end as any).toDate() : new Date(newData.end as any);
                        
                        createAttendanceSync(transaction, basePath, shift.id, newData.employeeId, newData.type, start, end, empData?.availability);
                    }
                }

                transaction.update(shiftRef, removeUndefined(newData));
            });
        } catch (e) {
            console.error("Update shift failed:", e);
        }
    };

    const deleteShift = async (id: string) => {
        const basePath = getBasePath();
        if (!basePath) return;

        const shiftRef = doc(db, basePath, 'shifts', id);
        
        // Fetch association attendance records to delete them too
        const attQuery = query(collection(db, basePath, 'attendance'), where('shiftId', '==', id));
        const attSnapshot = await getDocs(attQuery);

        try {
            await runTransaction(db, async (transaction) => {
                const shiftDoc = await transaction.get(shiftRef);
                if (!shiftDoc.exists()) return; // Already deleted

                const shiftData = shiftDoc.data();

                // Auto-Refund logic: If deleting an approved holiday, refund the days
                if (shiftData.type === 'holiday' && shiftData.status === 'approved') {
                    const empRef = doc(db, basePath, 'employees', shiftData.employeeId);
                    const empDoc = await transaction.get(empRef);
                    if (empDoc.exists()) {
                        const employee = empDoc.data() as Employee;
                        const start = (shiftData.start as any)?.toDate ? (shiftData.start as any).toDate() : new Date(shiftData.start);
                        const end = (shiftData.end as any)?.toDate ? (shiftData.end as any).toDate() : new Date(shiftData.end);

                        const daysToRefund = calculateWorkingDays(start, end, employee.availability);
                        const newBalance = (employee.holidaysAvailable || 0) + daysToRefund;
                        transaction.update(empRef, { holidaysAvailable: newBalance });
                    }
                }

                // Delete associated attendance records (for sick/holiday)
                attSnapshot.docs.forEach(d => transaction.delete(d.ref));
                
                transaction.delete(shiftRef);
            });
        } catch (e) {
            console.error("Delete shift failed", e);
            // Fallback for simple delete if transaction fails
            await deleteDoc(shiftRef).catch(() => { });
        }
    };

    const publishShifts = async (shiftIds: string[]) => {
        const basePath = getBasePath();
        if (!basePath) return;

        const CHUNK_SIZE = 450;
        const chunks = [];
        for (let i = 0; i < shiftIds.length; i += CHUNK_SIZE) {
            chunks.push(shiftIds.slice(i, i + CHUNK_SIZE));
        }

        for (const chunk of chunks) {
            const batch = writeBatch(db);
            chunk.forEach(id => {
                const ref = doc(db, basePath, 'shifts', id);
                batch.update(ref, { status: 'approved' });
            });
            await batch.commit();
        }
    };

    const deleteShiftSeries = async (groupId: string, fromDate: string) => {
        const basePath = getBasePath();
        if (!basePath) return;

        const fromDateObj = new Date(fromDate);
        const shiftsToDelete = shifts.filter(s => 
            s.recurrence?.groupId === groupId && 
            new Date(s.start) >= fromDateObj
        );

        if (shiftsToDelete.length === 0) return;

        const CHUNK_SIZE = 450;
        for (let i = 0; i < shiftsToDelete.length; i += CHUNK_SIZE) {
            const chunk = shiftsToDelete.slice(i, i + CHUNK_SIZE);
            const batch = writeBatch(db);
            chunk.forEach(s => {
                const ref = doc(db, basePath, 'shifts', s.id);
                batch.delete(ref);
            });
            await batch.commit();
        }
    };

    const toggleEmployeeStatus = async (
        employeeId: string,
        action: TimekeepingAction,
        method: string,
        location?: { lat: number, lng: number, accuracy?: number },
        bypassReason?: string,
        photoUrl?: string,
        wifiSSID?: string
    ) => {
        // WiFi Verification Logic
        if (settings.wifiVerificationEnabled && settings.requiredWifiSSID) {
            if (!wifiSSID || wifiSSID !== settings.requiredWifiSSID) {
                console.error(`WiFi Verification Failed: Expected ${settings.requiredWifiSSID}, got ${wifiSSID || 'None'}`);
                throw new Error(`Connection Error: This action requires connection to "${settings.requiredWifiSSID}".`);
            }
        }
        const basePath = getBasePath();
        if (!basePath) {
            console.error("Firebase BasePath is missing - verification failed.");
            return { offline: true };
        }

        // Offline: persist to the IndexedDB queue. The UI updates automatically because
        // `employees` / `attendance` / `breaks` replay the queue (survives app restarts).
        const queueOffline = async () => {
            await savePendingAction({
                employeeId,
                action,
                verificationMethod: method,
                timestamp: new Date().toISOString(),
                location,
                geofenceBypassReason: bypassReason,
                photoUrl: photoUrl,
                wifiSSID: wifiSSID
            });
            await refreshPendingActions();
            return { offline: true };
        };

        if (!isOnline) {
            return queueOffline();
        }

        // Ordering guard: if this employee still has unsynced offline actions, sync them first.
        // Otherwise e.g. an online "Start Break" would hit the server before the queued "Clock In".
        const hasPendingForEmployee = async () =>
            (await getPendingActions()).some(p => p.employeeId === employeeId);
        if (await hasPendingForEmployee()) {
            await triggerSync();
            if (await hasPendingForEmployee()) {
                return queueOffline();
            }
        }

        // Online logic
        const now = new Date();
        const nowStr = now.toISOString();

        try {
            const empRef = doc(db, basePath, 'employees', employeeId);
            const empData = employees.find(e => e.id === employeeId) || null;
            const newStatus: EmployeeStatus = getNewStatus(action);
            
            // 1. Initial Data Fetch (Robust)
            const allAttQuery = query(
                collection(db, basePath, 'attendance'),
                where('employeeId', '==', employeeId)
            );
            // Connectivity gate: confirm the server is really reachable BEFORE any write.
            // Handles "WiFi connected but no internet" — queue offline instead of hanging.
            let allAttSnap: any;
            try {
                allAttSnap = await withTimeout(getDocsFromServer(allAttQuery), 8000);
            } catch (netErr: any) {
                if (netErr?.message === NETWORK_TIMEOUT || netErr?.code === 'unavailable') {
                    console.warn('Server unreachable — queuing action offline.', netErr);
                    return queueOffline();
                }
                throw netErr;
            }
            const activeSessions = allAttSnap.docs.filter(d => d.data().status === 'active');
            
            // 2. Status Update (Individual)
            const statusUpdate: any = { status: newStatus };
            if (action === 'Start Break') statusUpdate.breakStartedAt = nowStr;
            if (action === 'End Break' || action === 'Clock Out') statusUpdate.breakStartedAt = null;
            await updateDoc(empRef, statusUpdate);

            // 3. Audit Log (Individual)
            await addDoc(collection(db, basePath, 'auditLog'), removeUndefined({
                employeeName: empData ? `${empData.firstName} ${empData.lastName}` : 'Unknown',
                action,
                verificationMethod: method,
                timestamp: nowStr
            }));

            // 4. Action-Specific Logic
            if (action === 'Clock In') {
                // Deduplication: Close any previous active sessions
                for (const snap of activeSessions) {
                    await updateDoc(snap.ref, {
                        clockOut: nowStr,
                        clockOutMethod: 'System Auto-Close (Deduplication)',
                        status: 'completed'
                    });
                }

                // Resolve location and site reference if a site exists
                const defaultSiteIn = (settings.locations || []).find((s: any) => Array.isArray(s.assignedEmployeeIds) && s.assignedEmployeeIds.includes(employeeId))
                    || (settings.locations || []).find((s: any) => s.isPrimary)
                    || (settings.locations || [])[0];
                const resolvedLocationId = (location as any)?.siteId || (defaultSiteIn ? defaultSiteIn.id : null);
                const resolvedLocationName = (location as any)?.siteName || (defaultSiteIn ? (bypassReason ? `${defaultSiteIn.name} (Off-Site)` : defaultSiteIn.name) : (bypassReason ? 'Off-Site Location' : null));

                // Create new attendance record
                const attRef = await addDoc(collection(db, basePath, 'attendance'), removeUndefined({
                    employeeId,
                    clockIn: nowStr,
                    clockInMethod: method,
                    clockOut: null,
                    status: 'active',
                    createdAt: nowStr,
                    location: location || null,
                    locationId: resolvedLocationId,
                    locationName: resolvedLocationName,
                    geofenceBypassReason: bypassReason || null,
                    clockInPhotoUrl: photoUrl || null,
                    approvalStatus: bypassReason ? 'pending' : 'approved'
                }));
                const newAttId = attRef.id;
                // Update doc with its own ID (common pattern in this codebase)
                await updateDoc(attRef, { id: newAttId });

                // Clock-in Notification
                const isBypass = !!bypassReason;
                if (isBypass || notificationSettings.clockInOut) {
                    const message = isBypass 
                        ? `⚠️ Geofence Bypass (Clock In): ${empData?.firstName || 'Employee'}. Reason: "${bypassReason}"`
                        : `${empData?.firstName || 'Employee'} clocked in.`;

                    await addDoc(collection(db, basePath, 'notifications'), {
                        title: isBypass ? '⚠️ Geofence Bypass Alert' : '⏱️ Staff Clock-In',
                        message,
                        type: isBypass ? 'geofence_bypass' : 'clock_in_out',
                        timestamp: nowStr,
                        isRead: false,
                        employeeId,
                        relatedId: newAttId,
                        recipient: 'manager',
                        recipientRole: 'manager'
                    });
                }

                // Slack Channel Notification
                const empFullName = empData ? `${empData.firstName} ${empData.lastName}` : 'Employee';
                if (bypassReason) {
                    notifySlackGeofenceBypass(settings, empFullName, 'In', bypassReason).catch(console.error);
                } else {
                    notifySlackClockIn(settings, empFullName, empData?.position).catch(console.error);
                }

                // Late Arrival Notification
                if (notificationSettings.lateArrivals) {
                    const timezone = settings?.timezone || 'Europe/London';
                    const todayStr = getDateInTimeZone(nowStr, timezone);
                    
                    // Prevent false positives: Only trigger late alert on the FIRST clock-in of the day.
                    // If they already clocked in today, they are returning from a break/errand.
                    const hasPriorAttendanceToday = allAttSnap.docs.some(d => {
                        const att = d.data();
                        // Ignore active sessions that we are about to close in deduplication
                        if (att.status === 'active') return false; 
                        return att.clockIn && getDateInTimeZone(att.clockIn, timezone) === todayStr;
                    });

                    if (!hasPriorAttendanceToday) {
                        const scheduledShift = shifts.find(s => 
                            s.employeeId === employeeId && 
                            s.type === 'work' && 
                            getDateInTimeZone(s.start, timezone) === todayStr &&
                            new Date(s.end).getTime() > now.getTime() // Shift hasn't ended yet
                        );
                        if (scheduledShift) {
                            const shiftStart = new Date(scheduledShift.start);
                            const graceMinutes = settings?.gracePeriod !== undefined ? settings.gracePeriod : 5;
                            const gracePeriodMs = graceMinutes * 60 * 1000;
                            if (now.getTime() > (shiftStart.getTime() + gracePeriodMs)) {
                                // Check Firestore directly to prevent duplicate kiosk notifications
                                const existingNotifQuery = query(
                                    collection(db, basePath, 'notifications'),
                                    where('type', '==', 'late_arrival'),
                                    where('employeeId', '==', employeeId)
                                );
                                const existingSnap = await getDocs(existingNotifQuery);
                                const todayNotifExists = existingSnap.docs.some(d => {
                                    const n = d.data();
                                    return n.timestamp && getDateInTimeZone(n.timestamp, timezone) === todayStr;
                                });
                                
                                if (!todayNotifExists) {
                                    const minsLate = Math.round((now.getTime() - shiftStart.getTime()) / (1000 * 60));
                                    await addDoc(collection(db, basePath, 'notifications'), {
                                        title: '⚠️ Late Arrival Alert',
                                        message: `${empData?.firstName || 'Employee'} arrived late (${minsLate} mins late).`,
                                        type: 'late_arrival',
                                        timestamp: nowStr,
                                        isRead: false,
                                        isDismissed: false,
                                        employeeId,
                                        relatedId: newAttId,
                                        recipient: 'manager',
                                        recipientRole: 'manager'
                                    });
                                }
                            }
                        }
                    }
                }
            } else if (action === 'Clock Out') {
                let activeAtt = activeSessions.length > 0 ? activeSessions[0] : null;

                // Fallback: If no doc with status === 'active', find the latest open attendance record without a clockOut
                if (!activeAtt) {
                    const openRecord = allAttSnap.docs
                        .map((d: any) => ({ id: d.id, doc: d, data: d.data() }))
                        .sort((a: any, b: any) => new Date(b.data.clockIn || 0).getTime() - new Date(a.data.clockIn || 0).getTime())
                        .find((d: any) => !d.data.clockOut);
                    if (openRecord) {
                        activeAtt = openRecord.doc;
                    }
                }

                if (activeAtt) {
                    const attRef = doc(db, basePath, 'attendance', activeAtt.id);
                    
                    const defaultSiteOut = (settings.locations || []).find((s: any) => Array.isArray(s.assignedEmployeeIds) && s.assignedEmployeeIds.includes(employeeId))
                        || (settings.locations || []).find((s: any) => s.isPrimary)
                        || (settings.locations || [])[0];
                    const resolvedOutLocationId = (location as any)?.siteId || activeAtt.data()?.locationId || (defaultSiteOut ? defaultSiteOut.id : null);
                    const resolvedOutLocationName = (location as any)?.siteName || (defaultSiteOut ? (bypassReason ? `${defaultSiteOut.name} (Off-Site)` : defaultSiteOut.name) : (bypassReason ? 'Off-Site Location' : (activeAtt.data()?.locationName || null)));

                    const updatePayload: any = {
                        clockOut: nowStr,
                        clockOutMethod: method,
                        clockOutLocation: location || null,
                        clockOutLocationId: resolvedOutLocationId,
                        clockOutLocationName: resolvedOutLocationName,
                        clockOutGeofenceBypassReason: bypassReason || null,
                        clockOutPhotoUrl: photoUrl || null,
                        status: 'completed'
                    };
                    if (bypassReason) {
                        updatePayload.approvalStatus = 'pending';
                    }
                    
                    await updateDoc(attRef, removeUndefined(updatePayload));

                    // Close any active breaks for this specific attendance record
                    const breakQuery = query(
                        collection(db, basePath, 'breaks'),
                        where('attendanceId', '==', activeAtt.id),
                        where('end', '==', null)
                    );
                    const breaksSnap = await getDocs(breakQuery);
                    for (const snap of breaksSnap.docs) {
                        await updateDoc(snap.ref, { end: nowStr });
                    }

                    const isBypass = !!bypassReason;
                    if (isBypass || notificationSettings.clockInOut) {
                        const message = isBypass 
                            ? `⚠️ Geofence Bypass (Clock Out): ${empData?.firstName || 'Employee'}. Reason: "${bypassReason}"`
                            : `${empData?.firstName || 'Employee'} clocked out.`;

                        await addDoc(collection(db, basePath, 'notifications'), {
                            title: isBypass ? '⚠️ Geofence Bypass Alert' : '⏱️ Staff Clock-Out',
                            message,
                            type: isBypass ? 'geofence_bypass' : 'clock_in_out',
                            timestamp: nowStr,
                            isRead: false,
                            employeeId,
                            relatedId: activeAtt.id,
                            recipient: 'manager',
                            recipientRole: 'manager'
                        });
                    }

                    // Slack Channel Notification for Clock Out / Bypass
                    const empFullNameOut = empData ? `${empData.firstName} ${empData.lastName}` : 'Employee';
                    if (bypassReason) {
                        notifySlackGeofenceBypass(settings, empFullNameOut, 'Out', bypassReason).catch(console.error);
                    } else {
                        let durationFormatted = 'Shift Ended';
                        const attClockIn = activeAtt.data()?.clockIn;
                        if (attClockIn) {
                            const diffMs = now.getTime() - new Date(attClockIn).getTime();
                            const hrs = Math.floor(diffMs / (1000 * 60 * 60));
                            const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                            durationFormatted = `${hrs}h ${mins}m`;
                        }
                        notifySlackClockOut(settings, empFullNameOut, durationFormatted).catch(console.error);
                    }

                    // Early Clock Out Notification
                    if (notificationSettings.earlyClockOuts) {
                        const timezone = settings?.timezone || 'Europe/London';
                        const todayStr = getDateInTimeZone(nowStr, timezone);
                        const scheduledShift = shifts.find(s => 
                            s.employeeId === employeeId && 
                            s.type === 'work' && 
                            getDateInTimeZone(s.start, timezone) === todayStr
                        );
                        if (scheduledShift) {
                            const shiftEnd = new Date(scheduledShift.end);
                            const earlyMinutes = (shiftEnd.getTime() - now.getTime()) / (1000 * 60);
                            if (earlyMinutes > (notificationSettings.earlyClockOutMinutes || 15)) {
                                await addDoc(collection(db, basePath, 'notifications'), {
                                    title: '⚠️ Early Clock-Out Alert',
                                    message: `${empData?.firstName || 'Employee'} clocked out early (${Math.round(earlyMinutes)} mins before shift end).`,
                                    type: 'early_clock_out',
                                    timestamp: nowStr,
                                    isRead: false,
                                    employeeId,
                                    relatedId: activeAtt.id,
                                    recipient: 'manager',
                                    recipientRole: 'manager'
                                });
                            }
                        }
                    }
                }
            } else if (action === 'Start Break') {
                if (activeSessions.length > 0) {
                    const activeAttId = activeSessions[0].id;
                    await addDoc(collection(db, basePath, 'breaks'), {
                        attendanceId: activeAttId,
                        start: nowStr,
                        end: null
                    });
                }
            } else if (action === 'End Break') {
                if (activeSessions.length > 0) {
                    const activeAttId = activeSessions[0].id;
                    const breakQuery = query(
                        collection(db, basePath, 'breaks'),
                        where('attendanceId', '==', activeAttId),
                        where('end', '==', null)
                    );
                    const breaksSnap = await getDocs(breakQuery);
                    for (const snap of breaksSnap.docs) {
                        await updateDoc(snap.ref, { end: nowStr });
                    }
                }
            }

            return { offline: false };
        } catch (error) {
            console.error("Firestore individual error in toggleEmployeeStatus:", error);
            throw error;
        }
    };

    const updateAttendance = async (id: string, data: Partial<Attendance>) => {
        const basePath = getBasePath();
        if (!basePath) return;
        await updateDoc(doc(db, basePath, 'attendance', id), removeUndefined(data));
    };

    const addTask = async (task: Omit<Task, 'id'>) => {
        if (isTrialExpired) throw new Error("Trial expired. Please upgrade to add tasks.");
        const basePath = getBasePath();
        if (!basePath) return;
        const taskCollection = collection(db, basePath, 'tasks');
        const taskDocRef = doc(taskCollection);
        await setDoc(taskDocRef, removeUndefined({
            ...task,
            id: taskDocRef.id
        }));
    };

    const updateTask = async (task: Task) => {
        const basePath = getBasePath();
        if (!basePath) return;
        await updateDoc(doc(db, basePath, 'tasks', task.id), removeUndefined(task));
    };

    const deleteTask = async (id: string) => {
        const basePath = getBasePath();
        if (!basePath) return;
        await deleteDoc(doc(db, basePath, 'tasks', id));
    };

    const addLogbookEntry = async (entry: Omit<LogbookEntry, 'id' | 'createdBy' | 'createdAt'>) => {
        if (isTrialExpired) throw new Error("Trial expired. Please upgrade to add logbook entries.");
        const basePath = getBasePath();
        if (!basePath) return;
        const logCollection = collection(db, basePath, 'logbook');
        const logDocRef = doc(logCollection);
        await setDoc(logDocRef, removeUndefined({
            ...entry,
            id: logDocRef.id,
            createdBy: currentUser?.displayName || 'Manager',
            createdAt: new Date().toISOString()
        }));
    };

    const updateLogbookEntry = async (entry: LogbookEntry) => {
        const basePath = getBasePath();
        if (!basePath) return;
        await updateDoc(doc(db, basePath, 'logbook', entry.id), removeUndefined(entry));
    };

    const deleteLogbookEntry = async (id: string) => {
        const basePath = getBasePath();
        if (!basePath) return;
        await deleteDoc(doc(db, basePath, 'logbook', id));
    };

    const updateCompanyProfile = async (data: { name?: string; logo?: string | null; managerPin?: string; phone?: string; subscription?: Partial<Subscription> }) => {
        const basePath = getBasePath();
        if (!basePath) return;
        
        const updateData: any = { ...data };
        if (data.subscription) {
            // Flatten subscription updates to avoid overwriting the whole object if needed, 
            // but here we likely want to merge. Firestore update with dot notation is better.
            Object.keys(data.subscription).forEach(key => {
                updateData[`subscription.${key}`] = (data.subscription as any)[key];
            });
            delete updateData.subscription;
        }

        await updateDoc(doc(db, basePath, 'company', 'profile'), removeUndefined(updateData));
    };

    const updateSettings = async (newSettings: AppSettings) => {
        const basePath = getBasePath();
        if (!basePath) return;

        // Backend Safeguard: If enabling NFC Verification, check if a master tag UID, site tag UID, or registered company tag is configured
        if (newSettings.nfcVerificationEnabled) {
            const hasRegisteredTag = (newSettings.nfcTags || []).length > 0;
            const hasMasterTag = !!newSettings.nfcVerificationUid?.trim();
            const hasSiteTag = (newSettings.locations || []).some((loc: any) => loc?.nfcUid?.trim());
            if (!hasRegisteredTag && !hasMasterTag && !hasSiteTag) {
                console.warn("NFC Verification enabled without any configured NFC tag UID. Reverting setting to disabled.");
                newSettings.nfcVerificationEnabled = false;
            }
        }

        await setDoc(doc(db, basePath, 'company', 'profile'), { settings: removeUndefined(newSettings) }, { merge: true });
    };

    const updateNotificationSettings = async (newSettings: Partial<NotificationSettings>) => {
        const basePath = getBasePath();
        if (!basePath) return;
        await updateDoc(doc(db, basePath, 'company', 'profile'), { notificationSettings: removeUndefined({ ...notificationSettings, ...newSettings }) });
    };

    const updateSubscription = async (plan: string, seats: number = 5, cycle: string = 'monthly') => {
        const basePath = getBasePath();
        if (!basePath) return;
        // In a real app, this would be a backend call to Stripe
        await updateDoc(doc(db, basePath, 'company', 'profile'), {
            subscription: {
                status: plan as any,
                employeeLimit: seats,
                billingCycle: cycle as any,
                renewalDate: new Date(Date.now() + (cycle === 'monthly' ? 2592000000 : 31536000000)).toISOString(),
                cancelAtPeriodEnd: false
            }
        });
    };

    const openBillingPortal = async () => {
        const basePath = getBasePath();
        if (!basePath) return;

        try {
            const functions = getFunctions(undefined, 'europe-west2');
            const createPortalSession = httpsCallable(functions, 'createPortalSession');

            const { data } = await createPortalSession({
                customerId: subscription?.stripeCustomerId,
                returnUrl: window.location.origin + '/#/app/subscription',
            }) as { data: { url: string } };

            if (data?.url) {
                window.location.assign(data.url);
            }
        } catch (e: any) {
            console.error("Portal Error:", e);
        }
    };

    const reactivateSubscription = async () => {
        const basePath = getBasePath();
        if (!basePath) return;

        try {
            const functions = getFunctions(undefined, 'europe-west2');
            if (subscription?.stripeSubscriptionId) {
                const cancelSub = httpsCallable(functions, 'cancelSubscription');
                await cancelSub({
                    subscriptionId: subscription.stripeSubscriptionId,
                    cancelAtPeriodEnd: false
                });
                await updateDoc(doc(db, basePath), {
                    'subscription.cancelAtPeriodEnd': false
                });
            } else {
                await openBillingPortal();
            }
        } catch (e: any) {
            console.error("Reactivate Error:", e);
            throw e;
        }
    };

    const cancelSubscription = async () => {
        const basePath = getBasePath();
        if (!basePath) return;

        try {
            const functions = getFunctions(undefined, 'europe-west2');
            if (subscription?.stripeSubscriptionId) {
                const cancelSub = httpsCallable(functions, 'cancelSubscription');
                await cancelSub({
                    subscriptionId: subscription.stripeSubscriptionId,
                    cancelAtPeriodEnd: true
                });
                await updateDoc(doc(db, basePath), {
                    'subscription.cancelAtPeriodEnd': true
                });
            } else {
                await openBillingPortal();
            }
        } catch (e: any) {
            console.error("Cancel Subscription Error:", e);
            throw e;
        }
    };

    const markNotificationAsRead = async (id: string) => {
        const basePath = getBasePath();
        if (!basePath) return;
        await updateDoc(doc(db, basePath, 'notifications', id), { isRead: true });
    };

    const markAllNotificationsAsRead = async () => {
        const basePath = getBasePath();
        if (!basePath) return;
        const batch = writeBatch(db);
        notifications.forEach(n => {
            if (!n.isRead) {
                batch.update(doc(db, basePath, 'notifications', n.id), { isRead: true });
            }
        });
        await batch.commit();
    };

    const deleteNotification = async (id: string) => {
        const basePath = getBasePath();
        if (!basePath) return;
        await updateDoc(doc(db, basePath, 'notifications', id), { isDismissed: true });
    };

    const clearAllNotifications = async () => {
        const basePath = getBasePath();
        if (!basePath) return;

        const batch = writeBatch(db);
        notifications.forEach(n => {
            if (!n.isDismissed) {
                batch.update(doc(db, basePath, 'notifications', n.id), { isDismissed: true });
            }
        });
        await batch.commit();
    };

    const exportAuditLog = async (log: AuditLogEntry[]) => {
        const csv = Papa.unparse(log);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        await downloadOrShareFile({
            filename: 'audit_log.csv',
            blob,
            mimeType: 'text/csv'
        });
    };

    const updateSalesRecord = async (weekStartDate: string, amount: number) => {
        const basePath = getBasePath();
        if (!basePath) return;

        const q = query(collection(db, basePath, 'sales'), where('weekStartDate', '==', weekStartDate));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
            await updateDoc(snapshot.docs[0].ref, { amount });
        } else {
            const salesCollection = collection(db, basePath, 'sales');
            const salesDocRef = doc(salesCollection);
            await setDoc(salesDocRef, { 
                id: salesDocRef.id,
                weekStartDate, 
                amount 
            });
        }
    };

    const requestTimeOff = async (data: { employeeId: string; type: 'holiday' | 'sick'; startDate: string; endDate: string; reason: string }) => {
        const basePath = getBasePath();
        if (!basePath) return;

        // Create shift with pending status
        const start = new Date(data.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(data.endDate);
        end.setHours(23, 59, 59, 999);

        const shiftCollection = collection(db, basePath, 'shifts');
        const shiftDocRef = doc(shiftCollection);
        await setDoc(shiftDocRef, {
            id: shiftDocRef.id,
            employeeId: data.employeeId,
            type: data.type,
            start: Timestamp.fromDate(start),
            end: Timestamp.fromDate(end),
            status: 'pending',
            reason: data.reason
        });

        // Notification
        const emp = employees.find(e => e.id === data.employeeId);
        const notifCollection = collection(db, basePath, 'notifications');
        const notifDocRef = doc(notifCollection);
        await setDoc(notifDocRef, {
            id: notifDocRef.id,
            message: `${emp?.firstName} requested ${data.type} leave.`,
            type: 'time_off_request',
            timestamp: new Date().toISOString(),
            isRead: false,
            employeeId: data.employeeId,
            shiftId: shiftDocRef.id
        });

        // Slack Notification
        const empNameTimeOff = emp ? `${emp.firstName} ${emp.lastName}` : 'Employee';
        const dateRangeStr = `${new Date(data.startDate).toLocaleDateString()} - ${new Date(data.endDate).toLocaleDateString()}`;
        notifySlackTimeOff(settings, empNameTimeOff, dateRangeStr, data.reason).catch(console.error);
    };

    // Helper to create attendance records for sick/holiday shifts
    const createAttendanceSync = (
        writer: { set: (ref: any, data: any) => any }, 
        basePath: string, 
        shiftId: string, 
        employeeId: string, 
        type: string, 
        start: Date, 
        end: Date, 
        availability?: Availability
    ) => {
        const curr = new Date(start);
        curr.setHours(0, 0, 0, 0);
        const endDateMidnight = new Date(end);
        endDateMidnight.setHours(0, 0, 0, 0);

        while (curr <= endDateMidnight) {
            const dayIndex = curr.getDay();
            const daysMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const dayKey = daysMap[dayIndex] as keyof Availability;
            
            let isWorkDay = true;
            if (availability) {
                isWorkDay = availability[dayKey]?.isAvailable ?? false;
            } else {
                isWorkDay = dayIndex !== 0 && dayIndex !== 6;
            }

            if (isWorkDay) {
                const attRef = doc(collection(db, basePath, 'attendance'));
                const clockIn = new Date(curr);
                clockIn.setHours(9, 0, 0, 0);
                const clockOut = new Date(curr);
                clockOut.setHours(17, 0, 0, 0);

                writer.set(attRef, {
                    id: attRef.id,
                    employeeId,
                    clockIn: clockIn.toISOString(),
                    clockOut: clockOut.toISOString(),
                    status: 'completed',
                    approvalStatus: 'approved',
                    rating: 5,
                    shiftId,
                    clockInMethod: `Approved ${type}`,
                    clockOutMethod: `Approved ${type}`,
                    createdAt: new Date().toISOString()
                });
            }
            curr.setDate(curr.getDate() + 1);
        }
    };

    const approveTimeOffRequest = async (shiftId: string) => {
        const basePath = getBasePath();
        if (!basePath) return;

        try {
            await runTransaction(db, async (transaction) => {
                const shiftRef = doc(db, basePath, 'shifts', shiftId);
                const shiftDoc = await transaction.get(shiftRef);
                if (!shiftDoc.exists()) throw new Error("Shift not found");

                const shiftData = shiftDoc.data();
                if (shiftData.status === 'approved') return; // Already approved

                const start = shiftData.start?.toDate ? shiftData.start.toDate() : new Date(shiftData.start);
                const end = shiftData.end?.toDate ? shiftData.end.toDate() : new Date(shiftData.end);

                // Auto-Deduction Logic: If approved holiday, subtract days
                if (shiftData.type === 'holiday' || shiftData.type === 'sick') {
                    const empRef = doc(db, basePath, 'employees', shiftData.employeeId);
                    const empDoc = await transaction.get(empRef);
                    if (empDoc.exists()) {
                        const empData = empDoc.data() as Employee;
                        
                        if (shiftData.type === 'holiday') {
                            const days = calculateWorkingDays(start, end, empData.availability);
                            const currentBalance = empData.holidaysAvailable || 0;
                            transaction.update(empRef, { holidaysAvailable: currentBalance - days });
                        }

                        // Create Attendance Records for each working day
                        createAttendanceSync(transaction, basePath, shiftId, shiftData.employeeId, shiftData.type, start, end, empData.availability);
                    }
                }

                transaction.update(shiftRef, { status: 'approved' });
            });
        } catch (e) {
            console.error("Error approving request:", e);
        }
    };

    const denyTimeOffRequest = async (shiftId: string) => {
        const basePath = getBasePath();
        if (!basePath) return;

        try {
            await runTransaction(db, async (transaction) => {
                const shiftRef = doc(db, basePath, 'shifts', shiftId);
                const shiftDoc = await transaction.get(shiftRef);
                if (!shiftDoc.exists()) return;

                const shiftData = shiftDoc.data();

                // Auto-Refund Logic: If was approved holiday, add days back
                if (shiftData.type === 'holiday' && shiftData.status === 'approved') {
                    const empRef = doc(db, basePath, 'employees', shiftData.employeeId);
                    const empDoc = await transaction.get(empRef);
                    if (empDoc.exists()) {
                        const empData = empDoc.data() as Employee;
                        const start = shiftData.start?.toDate ? shiftData.start.toDate() : new Date(shiftData.start);
                        const end = shiftData.end?.toDate ? shiftData.end.toDate() : new Date(shiftData.end);

                        const days = calculateWorkingDays(start, end, empData.availability);
                        const currentBalance = empData.holidaysAvailable || 0;
                        transaction.update(empRef, { holidaysAvailable: currentBalance + days });
                    }
                }

                transaction.update(shiftRef, { status: 'denied' });
            });
        } catch (e) {
            console.error("Error denying request:", e);
        }
    };

    const approveShiftSwap = async (notification: Notification) => {
        const basePath = getBasePath();
        if (!basePath) throw new Error("No workspace found");
        if (!notification.relatedId) throw new Error("Missing shift ID on notification");
        if (!notification.toId) throw new Error("Missing target employee ID on notification");

        // Step 1: Verify shift exists before updating
        const shiftRef = doc(db, basePath, 'shifts', notification.relatedId);
        const shiftSnap = await getDoc(shiftRef);
        if (!shiftSnap.exists()) throw new Error("Shift not found in database");

        // Step 2: Update the shift
        await updateDoc(shiftRef, removeUndefined({
            employeeId: notification.toId,
            status: 'approved',
            swapInfo: {
                fromId: notification.fromId,
                originalTime: notification.timestamp,
                autoApproved: false,
                approvedBy: currentUser?.uid,
                approvedAt: new Date().toISOString()
            }
        }));

        // Step 3: Dismiss the notification with status
        const notifRef = doc(db, basePath, 'notifications', notification.id);
        await updateDoc(notifRef, { isRead: true, isDismissed: true, status: 'approved' });

        // Step 4: Notify the requester
        if (notification.fromId) {
            await addDoc(collection(db, basePath, 'notifications'), removeUndefined({
                employeeId: notification.fromId,
                message: `Your swap request was approved by the manager.`,
                type: 'shift_swap_approved',
                timestamp: new Date().toISOString(),
                isRead: false,
                isDismissed: false,
                status: 'approved'
            }));
        }
    };

    const denyShiftSwap = async (notification: Notification) => {
        const basePath = getBasePath();
        if (!basePath) return;

        try {
            const notifRef = doc(db, basePath, 'notifications', notification.id);
            await updateDoc(notifRef, { isRead: true, isDismissed: true, status: 'denied' });
            
            // Optional: Notify requester of denial
            if (notification.fromId) {
                await addDoc(collection(db, basePath, 'notifications'), {
                    employeeId: notification.fromId,
                    message: `Your swap request was declined by the manager.`,
                    type: 'shift_swap_denied',
                    timestamp: new Date().toISOString(),
                    isRead: false,
                    isDismissed: false,
                    status: 'denied'
                });
            }
        } catch (e) {
            console.error("Error denying shift swap:", e);
            throw e;
        }
    };

    const requestShiftSwap = async (shiftId: string, fromId: string, toId: string, reason?: string): Promise<{ autoApproved: boolean }> => {
        const basePath = getBasePath();
        if (!basePath) return { autoApproved: false };

        const fromEmp = employees.find(e => e.id === fromId);
        const toEmp = employees.find(e => e.id === toId);
        const shift = shifts.find(s => s.id === shiftId);

        if (!fromEmp || !toEmp || !shift) return { autoApproved: false };

        // Trusted Swap Logic: Check for direct skill parity
        const fromSkills = [...(fromEmp.skills || [])].sort().join(',');
        const toSkills = [...(toEmp.skills || [])].sort().join(',');
        
        const skillsMatch = fromSkills === toSkills && toSkills.length > 0;

        if (skillsMatch) {
            await updateDoc(doc(db, basePath, 'shifts', shiftId), removeUndefined({ 
                employeeId: toId,
                status: 'approved',
                swapInfo: { fromId, originalTime: new Date().toISOString(), autoApproved: true }
            }));

            await addDoc(collection(db, basePath, 'notifications'), removeUndefined({
                message: `Autonomous Swap Approved: ${toEmp.firstName} has taken over the shift from ${fromEmp.firstName} (Skills match).`,
                type: 'shift_swap_auto',
                timestamp: new Date().toISOString(),
                isRead: false
            }));

            return { autoApproved: true };
        } else {
            await addDoc(collection(db, basePath, 'notifications'), removeUndefined({
                message: `Swap Request: ${fromEmp.firstName} wants to swap shift with ${toEmp.firstName}. Manager review required.`,
                type: 'shift_swap_request',
                timestamp: new Date().toISOString(),
                isRead: false,
                relatedId: shiftId,
                fromId,
                toId,
                reason
            }));

            return { autoApproved: false };
        }
    };

    const generateCalendarSyncToken = async (employeeId?: string): Promise<string> => {
        try {
            const functions = getFunctions(undefined, 'europe-west2');
            const generateToken = httpsCallable(functions, 'generateCalendarToken');
            const result = await generateToken({ employeeId });
            const token = (result.data as any).token;
            return token;
        } catch (e: any) {
            console.error("Error generating calendar token:", e);
            throw new Error(e.message || "Failed to generate calendar token.");
        }
    };

    const broadcastShift = async (shiftId: string) => {
        const basePath = getBasePath();
        if (!basePath) return;

        const shift = shifts.find(s => s.id === shiftId);
        if (!shift) return;

        const shiftEmp = employees.find(e => e.id === shift.employeeId);
        const shiftPosNorm = (shiftEmp?.position || '').trim().toLowerCase();
        const shiftDeptNorm = (shiftEmp?.department || '').trim().toLowerCase();

        // 1. Primary Filter: Position + No Conflict + Available
        let eligibleEmployees = employees.filter(emp => {
            if (emp.id === shift.employeeId) return false;

            // Position Check (normalized)
            const empPosNorm = (emp.position || '').trim().toLowerCase();
            if (shiftPosNorm && empPosNorm !== shiftPosNorm) return false;

            // Schedule Conflict Check
            const hasConflict = shifts.some(s => 
                s.employeeId === emp.id && 
                new Date(s.start) < new Date(shift.end) && 
                new Date(s.end) > new Date(shift.start)
            );
            if (hasConflict) return false;

            // Availability Check
            const dayOfWeek = new Date(shift.start).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() as keyof Availability;
            if (emp.availability && emp.availability[dayOfWeek]?.isAvailable === false) return false;

            return true;
        });

        // 2. Fallback Filter: If no exact position match, check Department or all non-conflicting active staff
        if (eligibleEmployees.length === 0) {
            eligibleEmployees = employees.filter(emp => {
                if (emp.id === shift.employeeId) return false;

                const empDeptNorm = (emp.department || '').trim().toLowerCase();
                if (shiftDeptNorm && empDeptNorm !== shiftDeptNorm) return false;

                const hasConflict = shifts.some(s => 
                    s.employeeId === emp.id && 
                    new Date(s.start) < new Date(shift.end) && 
                    new Date(s.end) > new Date(shift.start)
                );
                if (hasConflict) return false;

                const dayOfWeek = new Date(shift.start).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() as keyof Availability;
                if (emp.availability && emp.availability[dayOfWeek]?.isAvailable === false) return false;

                return true;
            });
        }

        // 3. Last Fallback: Any colleague without a schedule conflict
        if (eligibleEmployees.length === 0) {
            eligibleEmployees = employees.filter(emp => {
                if (emp.id === shift.employeeId) return false;
                const hasConflict = shifts.some(s => 
                    s.employeeId === emp.id && 
                    new Date(s.start) < new Date(shift.end) && 
                    new Date(s.end) > new Date(shift.start)
                );
                return !hasConflict;
            });
        }

        if (eligibleEmployees.length === 0) {
            throw new Error("No eligible employees found for this shift. Ensure other employees have no schedule conflicts during these hours.");
        }

        const batch = writeBatch(db);

        const swapRequestId = doc(collection(db, basePath, 'swapRequests')).id;
        const swapRequestRef = doc(db, basePath, 'swapRequests', swapRequestId);
        
        batch.set(swapRequestRef, {
            id: swapRequestId,
            shiftId,
            fromEmployeeId: shift.employeeId,
            targetEmployeeId: null,
            status: 'pending',
            createdAt: new Date().toISOString(),
            reason: "Auto-Broadcast for Replacement"
        });

        // Create notifications for all eligible employees
        eligibleEmployees.forEach(emp => {
            const notifRef = doc(collection(db, basePath, 'notifications'));
            batch.set(notifRef, {
                id: notifRef.id,
                employeeId: emp.id,
                message: `Shift Available: ${emp.firstName}, a ${shiftEmp?.position || 'colleague'} shift is open for ${new Date(shift.start).toLocaleDateString()}. Claim it now!`,
                type: 'shift_broadcast',
                timestamp: new Date().toISOString(),
                isRead: false,
                relatedId: swapRequestId, // Link to the swap request
                fromId: shift.employeeId
            });
        });

        await batch.commit();
    };

    const claimShift = async (swapRequestId: string, employeeId: string) => {
        const basePath = getBasePath();
        if (!basePath) return;

        try {
            await runTransaction(db, async (transaction) => {
                const swapRef = doc(db, basePath, 'swapRequests', swapRequestId);
                const swapSnap = await transaction.get(swapRef);
                
                if (!swapSnap.exists()) throw new Error("Swap request no longer exists.");
                const swapData = swapSnap.data();
                
                if (swapData.status !== 'pending') throw new Error("Shift has already been claimed.");

                const shiftRef = doc(db, basePath, 'shifts', swapData.shiftId);
                const shiftSnap = await transaction.get(shiftRef);
                if (!shiftSnap.exists()) throw new Error("Shift no longer exists.");

                const empRef = doc(db, basePath, 'employees', employeeId);
                const empSnap = await transaction.get(empRef);
                if (!empSnap.exists()) throw new Error("Employee not found.");
                const empData = empSnap.data() as Employee;

                // Update the shift
                transaction.update(shiftRef, {
                    employeeId: employeeId,
                    status: 'approved',
                    swapInfo: {
                        fromId: swapData.fromEmployeeId,
                        claimedAt: new Date().toISOString(),
                        autoApproved: true
                    }
                });

                // Update the swap request
                transaction.update(swapRef, {
                    status: 'auto_approved',
                    targetEmployeeId: employeeId
                });

                // Add Audit Log
                const auditRef = doc(collection(db, basePath, 'auditLog'));
                transaction.set(auditRef, {
                    id: auditRef.id,
                    employeeName: `${empData.firstName} ${empData.lastName}`,
                    action: 'Claimed Broadcasted Shift',
                    verificationMethod: 'System Transaction',
                    timestamp: new Date().toISOString()
                });

                // Notify Manager
                const managerNotifRef = doc(collection(db, basePath, 'notifications'));
                transaction.set(managerNotifRef, {
                    id: managerNotifRef.id,
                    message: `${empData.firstName} has claimed the broadcasted shift for ${new Date(shiftSnap.data().start).toLocaleDateString()}.`,
                    type: 'shift_swap_auto',
                    timestamp: new Date().toISOString(),
                    isRead: false
                });

                // Notify Original Employee
                if (swapData.fromEmployeeId) {
                    const originalEmpNotifRef = doc(collection(db, basePath, 'notifications'));
                    transaction.set(originalEmpNotifRef, {
                        id: originalEmpNotifRef.id,
                        employeeId: swapData.fromEmployeeId,
                        message: `Your broadcasted shift for ${new Date(shiftSnap.data().start).toLocaleDateString()} has been claimed by ${empData.firstName} ${empData.lastName}.`,
                        type: 'shift_swap_approved',
                        timestamp: new Date().toISOString(),
                        isRead: false
                    });
                }
            });
        } catch (e) {
            console.error("Claim shift failed:", e);
            throw e;
        }
    };

    const syncInProgressRef = useRef(false);
    const triggerSync = async () => {
        const basePath = getBasePath();
        // Lock: never run two syncs in parallel (prevents duplicate attendance records)
        if (!basePath || syncInProgressRef.current) return;
        syncInProgressRef.current = true;
        let allSucceeded = true;

        try {
        const actions = await getPendingActions();
        if (actions.length === 0) { allSucceeded = false; return; }

        // Real reachability probe (navigator.onLine is unreliable in Android WebView)
        try {
            await withTimeout(getDocsFromServer(query(collection(db, basePath, 'employees'), limit(1))), 8000);
        } catch {
            allSucceeded = false;
            return;
        }

        // Fix: Sort actions by timestamp to ensure chronological processing
        actions.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        for (const item of actions) {
            try {
                // Re-run status logic for online
                const now = new Date(item.timestamp);
                const batch = writeBatch(db);

                // 1. Update Employee
                const empRef = doc(db, basePath, 'employees', item.employeeId);
                const newStatus = getNewStatus(item.action);
                const empUpdate: any = { status: newStatus };
                if (item.action === 'Start Break') empUpdate.breakStartedAt = item.timestamp;
                if (item.action === 'End Break' || item.action === 'Clock Out') empUpdate.breakStartedAt = null;
                batch.update(empRef, empUpdate);

                // 2. Audit Log
                const auditRef = doc(collection(db, basePath, 'auditLog'));
                const emp = employees.find(e => e.id === item.employeeId);
                batch.set(auditRef, removeUndefined({
                    id: auditRef.id,
                    employeeName: emp ? `${emp.firstName} ${emp.lastName}` : 'Unknown',
                    action: item.action,
                    verificationMethod: item.verificationMethod + ' (Synced)',
                    timestamp: item.timestamp
                }));

                // 3. Attendance/Breaks Logic
                if (item.action === 'Clock In') {
                    // Deduplication: Close any previous active sessions for this employee before syncing the new one
                    const activeQuery = query(collection(db, basePath, 'attendance'), where('employeeId', '==', item.employeeId), where('status', '==', 'active'));
                    const activeSnap = await getDocs(activeQuery);
                    activeSnap.forEach(doc => {
                        batch.update(doc.ref, { 
                            clockOut: item.timestamp, 
                            clockOutMethod: 'Sync Deduplication',
                            status: 'completed' 
                        });
                    });

                    const attRef = doc(collection(db, basePath, 'attendance'));
                    batch.set(attRef, {
                        id: attRef.id,
                        employeeId: item.employeeId,
                        clockIn: item.timestamp,
                        clockInMethod: item.verificationMethod + ' (Synced)',
                        clockOut: null,
                        status: 'active',
                        createdAt: item.timestamp,
                        location: item.location || null,
                        geofenceBypassReason: item.geofenceBypassReason || null,
                        clockInPhotoUrl: item.photoUrl || null,
                        // Offline geofence bypasses must still go through manager approval
                        approvalStatus: item.geofenceBypassReason ? 'pending' : 'approved'
                    });
 
                    if (notificationSettings.clockInOut) {
                        const notifRef = doc(collection(db, basePath, 'notifications'));
                        batch.set(notifRef, {
                            id: notifRef.id,
                            message: `${emp?.firstName || 'Employee'} clocked in (Synced).`,
                            type: 'clock_in_out',
                            timestamp: item.timestamp,
                            isRead: false,
                            isDismissed: false,
                            employeeId: item.employeeId
                        });
                    }
                } else if (item.action === 'Clock Out') {
                    // Find active attendance. 
                    const attQuery = query(collection(db, basePath, 'attendance'), where('employeeId', '==', item.employeeId), where('status', '==', 'active'), limit(1));
                    const attSnap = await getDocs(attQuery);
                    if (!attSnap.empty) {
                        batch.update(attSnap.docs[0].ref, { 
                            clockOut: item.timestamp, 
                            clockOutMethod: item.verificationMethod + ' (Synced)',
                            clockOutLocation: item.location || null,
                            clockOutGeofenceBypassReason: item.geofenceBypassReason || null,
                            clockOutPhotoUrl: item.photoUrl || null,
                            ...(item.geofenceBypassReason ? { approvalStatus: 'pending' } : {}),
                            status: 'completed' 
                        });

                        if (notificationSettings.clockInOut) {
                            const notifRef = doc(collection(db, basePath, 'notifications'));
                            batch.set(notifRef, {
                                id: notifRef.id,
                                message: `${emp?.firstName || 'Employee'} clocked out (Synced).`,
                                type: 'clock_in_out',
                                timestamp: item.timestamp,
                                isRead: false,
                                isDismissed: false,
                                employeeId: item.employeeId
                            });
                        }

                        // Also close any open breaks attached to this attendance
                        const breakQuery = query(collection(db, basePath, 'breaks'), where('attendanceId', '==', attSnap.docs[0].id), where('end', '==', null));
                        const breakSnap = await getDocs(breakQuery);
                        breakSnap.forEach(b => {
                            batch.update(b.ref, { end: item.timestamp });
                        });
                    }
                } else if (item.action === 'Start Break') {
                    const attQuery = query(collection(db, basePath, 'attendance'), where('employeeId', '==', item.employeeId), where('status', '==', 'active'), limit(1));
                    const attSnap = await getDocs(attQuery);
                    if (!attSnap.empty) {
                    const brkRef = doc(collection(db, basePath, 'breaks'));
                    batch.set(brkRef, {
                        id: brkRef.id,
                        attendanceId: attSnap.docs[0].id,
                        start: item.timestamp,
                        end: null
                    });
                    }
                } else if (item.action === 'End Break') {
                    // Find active attendance
                    const attQuery = query(collection(db, basePath, 'attendance'), where('employeeId', '==', item.employeeId), where('status', '==', 'active'), limit(1));
                    const attSnap = await getDocs(attQuery);
                    if (!attSnap.empty) {
                        // Find open break for this attendance
                        const breakQuery = query(collection(db, basePath, 'breaks'), where('attendanceId', '==', attSnap.docs[0].id), where('end', '==', null), limit(1));
                        const breakSnap = await getDocs(breakQuery);
                        if (!breakSnap.empty) {
                            batch.update(breakSnap.docs[0].ref, { end: item.timestamp });
                        }
                    }
                }

                // Once commit() is called, Firestore's persistent cache guarantees delivery even
                // across restarts. If the server ack is slow, don't hang the sync lock forever.
                try {
                    await withTimeout(batch.commit(), 20000);
                } catch (commitErr: any) {
                    if (commitErr?.message !== NETWORK_TIMEOUT) throw commitErr;
                    console.warn('Sync commit ack timed out — write is queued in Firestore cache.', item.id);
                }
                await removePendingAction(item.id);
            } catch (e: any) {
                if (e?.code === 'not-found') {
                    // Employee was deleted — this action can never sync; drop it so it doesn't block the queue.
                    console.warn("Dropping unsyncable offline action (employee not found)", item);
                    await removePendingAction(item.id);
                    continue;
                }
                // Stop at the first failure to preserve chronological order
                // (never send a Clock Out / Break before its Clock In).
                console.error("Sync failed for item — will retry later", item, e);
                allSucceeded = false;
                break;
            }
        }
        } finally {
            syncInProgressRef.current = false;
            await refreshPendingActions(); // accurate badge count from the real queue
        }

        if (allSucceeded) {
            const nowStr = new Date().toISOString();
            setLastSyncTime(nowStr);
            localStorage.setItem('lastSyncTime', nowStr);
        }
    };

    // Keep a stable reference so network listeners are registered once (not every render)
    const triggerSyncRef = useRef(triggerSync);
    triggerSyncRef.current = triggerSync;

    useEffect(() => {
        // Platform-aware network status listener (single source of truth)
        const initNetwork = async () => {
            if (Capacitor.isNativePlatform()) {
                const status = await Network.getStatus();
                setIsOnline(status.connected);

                const handler = await Network.addListener('networkStatusChange', status => {
                    setIsOnline(status.connected);
                    if (status.connected) {
                        triggerSyncRef.current();
                    }
                });

                return () => {
                    handler.remove();
                };
            } else {
                // Browser fallback
                const handleStatusChange = () => {
                    setIsOnline(navigator.onLine);
                    if (navigator.onLine) triggerSyncRef.current();
                };
                window.addEventListener('online', handleStatusChange);
                window.addEventListener('offline', handleStatusChange);
                return () => {
                    window.removeEventListener('online', handleStatusChange);
                    window.removeEventListener('offline', handleStatusChange);
                };
            }
        };

        const cleanupPromise = initNetwork();
        return () => {
             cleanupPromise.then(cleanup => cleanup && cleanup());
        };
    }, []);

    const fetchAttendanceRange = useCallback(async (startDateISO: string, endDateISO: string): Promise<Attendance[]> => {
        const basePath = getBasePath();
        if (!basePath) return [];
        try {
            const attQuery = query(
                collection(db, basePath, 'attendance'),
                where('clockIn', '>=', startDateISO)
            );
            const snapshot = await getDocs(attQuery);
            return snapshot.docs
                .map(d => {
                    const data = d.data();
                    return {
                        ...data,
                        id: d.id,
                        clockIn: data.clockIn?.toDate ? data.clockIn.toDate().toISOString() : data.clockIn,
                        clockOut: data.clockOut?.toDate ? data.clockOut.toDate().toISOString() : data.clockOut
                    } as Attendance;
                })
                .filter(a => new Date(a.clockIn) <= new Date(endDateISO + 'T23:59:59'));
        } catch (e) {
            console.error("Error fetching historical attendance", e);
            return [];
        }
    }, [getBasePath]);

    return (
        <DataContext.Provider value={{
            employees, shifts, attendance, breaks, tasks, logbookEntries, notifications, auditLog, salesRecords, swapRequests,
            settings, notificationSettings, subscription,
            companyName, companyLogo, managerPin, managerPhone, calendarToken,
            isLoading, isOnline, pendingSyncCount, lastSyncTime, isPro, canAddEmployee, isTrialExpired, kpiData,
            addEmployee, updateEmployee, deleteEmployee, importEmployees, exportEmployees, generateCalendarSyncToken,
            addShift, addShiftsBatch, updateShift, deleteShift, deleteShiftSeries, publishShifts,
            toggleEmployeeStatus, updateAttendance,
            addTask, updateTask, deleteTask,
            addLogbookEntry, updateLogbookEntry, deleteLogbookEntry,
            updateCompanyProfile, updateSettings, updateNotificationSettings,
            updateSubscription, cancelSubscription, openBillingPortal, reactivateSubscription,
            markNotificationAsRead, markAllNotificationsAsRead,
            deleteNotification, clearAllNotifications,
            exportAuditLog, updateSalesRecord,
            requestTimeOff, approveTimeOffRequest, denyTimeOffRequest, 
            requestShiftSwap, approveShiftSwap, denyShiftSwap,
            broadcastShift, claimShift,
            triggerSync, fetchAttendanceRange,
            activeBroadcast
        }}>
            {children}
        </DataContext.Provider>
    );
};
